import gql from "graphql-tag";

const FRAGMENT_RICH_MEDIA_FIELD = gql`
  fragment RichMediaFields on RichMedia {
    id
    url
    sort
    createdDate
    updatedDate
  }
`;
const FRAGMENT_IMAGE_FIELD = gql`
  fragment ImageFields on Image {
    id
    sort
    url
    path
    width
    height
    createdDate
    updatedDate
    quality
    isPGMedia
  }
`;

const FRAGMENT_PRICE_FIELD = gql`
  fragment PriceFields on Price {
    fixed
    currencyCode
  }
`;

const FRAGMENT_MULTILINE_FIELD = gql`
  fragment MultiLangTextFields on MultiLangText {
    en_GB
    zh_HK
    zh_CN
    ms_MY
  }
`;

const FRAGMENT_REFERENCE_FIELD = gql`
  fragment ReferenceFields on Reference {
    id
    code
    label
    description
  }
`;

const BASE_LISTING_INFO_FRAGMENT = gql`
  fragment BaseListingInfoFragment on Listing {
    id
    amenities {
      ...ReferenceFields
    }
    bathroom
    bedroom
    bedroomRef {
      ...ReferenceFields
    }
    buildingFacilities {
      ...ReferenceFields
    }
    carPark
    channel {
      ...ReferenceFields
    }
    conditions {
      ...ReferenceFields
    }
    contentUpdatedDate
    description {
      ...MultiLangTextFields
    }
    direction {
      ...ReferenceFields
    }
    expiryDate
    extension {
      isSaleableAreaVerified
      propertyNo
      isMigrationUpdateRequired
      isWithinMigrationGracePeriod
    }
    attributes {
      isWithinMigrationGracePeriod
      mandatoryFields
      crossListTypeCode
      isRequiredUpdateLocationMandatory
      isRequiredUpdateLocationOptional
    }
    firstPublishedDate
    floorPlans {
      ...ImageFields
    }
    photo360s {
      ...ImageFields
    }
    propertyCategoryTypeCode
    floorZone {
      ...ReferenceFields
    }
    fullAddress {
      ...MultiLangTextFields
    }
    grossArea
    grossAreaMeasurement {
      ...ReferenceFields
    }
    image360s {
      ...RichMediaFields
    }
    images {
      ...ImageFields
    }
    layout {
      ...ReferenceFields
    }
    listerId
    listingProduct {
      ...ReferenceFields
    }
    listingRefNo
    location {
      level1 {
        id
        nanoId
        text {
          ...MultiLangTextFields
        }
      }
      level2 {
        id
        nanoId
        text {
          ...MultiLangTextFields
        }
      }
      level3 {
        id
        nanoId
        text {
          ...MultiLangTextFields
        }
      }
      level4 @include(if: $shouldExtendsFields) {
        id
        text {
          ...MultiLangTextFields
        }
      }
      level5 {
        id
        nanoId
        text {
          ...MultiLangTextFields
        }
      }
      address {
        ...MultiLangTextFields
      }
      floor
      unit
      latitude
      longitude
      hideUnitFloor
      carParkNo
      hideCarParkNo
      showPIN
      hideLocationLevel3
      postalCode
      block
      hiddenAddress
      hasNoTownship
    }
    nextRotationDate
    organisationId
    outdoorArea
    productExpiryDate
    productUpgradeDate
    propertyType {
      ...ReferenceFields
    }
    rentPrice {
      ...PriceFields
    }
    representationListerId
    saleableArea
    saleableAreaMeasurement {
      ...ReferenceFields
    }
    salePrice {
      ...PriceFields
    }
    status {
      ...ReferenceFields
    }
    storeRoom
    title {
      ...MultiLangTextFields
    }
    unitFeatures {
      ...ReferenceFields
    }
    videos {
      ...RichMediaFields
    }
    views {
      ...ReferenceFields
    }
    photo360players {
      ...RichMediaFields
    }
    config {
      createdBrand
      createdVersion
    }
  }
  ${FRAGMENT_IMAGE_FIELD}
  ${FRAGMENT_PRICE_FIELD}
  ${FRAGMENT_MULTILINE_FIELD}
  ${FRAGMENT_REFERENCE_FIELD}
  ${FRAGMENT_RICH_MEDIA_FIELD}
`;

export const REPORTING_QUERY = gql`
  query listingPerformance($listingId: ID!, $duration: String!) {
    listingPerformance(listingId: $listingId, duration: $duration) {
      listingId
      filter
      dateRange {
        startDate
        endDate
      }
      impression {
        total
        standard
        feature
        premium
        comparison {
          type
          level1 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
          level2 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
        }
        breakdown {
          city {
            label
            total
          }
          region {
            label
            total
          }
          area {
            label
            total
          }
          other {
            total
          }
        }
      }
      pageView {
        total
        app
        mobile
        web
        standard
        feature
        premium
        tier1 {
          name
          total
          app
          mobile
          web
        }
        tier2 {
          name
          total
          app
          mobile
          web
        }
        tier3 {
          name
          total
          app
          mobile
          web
        }
        comparison {
          type
          level1 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
          level2 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
        }
      }
      enquiries {
        total
        phone
        whatsapp
        email
        standard
        feature
        premium
        tier1 {
          name
          total
          phone
          whatsapp
          email
        }
        tier2 {
          name
          total
          phone
          whatsapp
          email
        }
        tier3 {
          name
          total
          phone
          whatsapp
          email
        }
        comparison {
          type
          level1 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
          level2 {
            name
            level
            count
            multiplier
            fromSize
            toSize
          }
        }
      }
      engagement {
        savedListing
        downloadPdf
        viewPhotos
        tour360
        viewFloorPlans
        viewVideos
        socialShare {
          total
          facebook
          whatsapp
          weibo
          wechat
          twitter
          linkedin
          googleplus
          link
          email
          other
        }
      }
    }
  }
`;

export const AGENTNET_LISTING_PERFORMANCE_REPORT_QUERY = gql`
  query agentNetListingPerformance(
    $listingId: String!
    $duration: String
    $startDate: String
    $endDate: String
  ) {
    agentNetListingPerformance(
      listingId: $listingId
      duration: $duration
      startDate: $startDate
      endDate: $endDate
    ) {
      data {
        listingId
        startDate
        endDate
        impression
        views
        leads
        leadsGroup {
          whatsapp
          phoneReveals
          sms
          call
          messages
          totalLeads
        }
        engagementsGroup {
          views
          galleryActions
          mapActions
          priceInsights
          descriptionExpands
          shares
          saves
          totalEngagement
        }
        repost
        startDate
        endDate
        impressionPercent
        leadsPercent
        viewsPercent
      }
    }
  }
`;

export const DISTRIBUTION_QUERY = gql`
  query listingGraph($listingId: ID!, $duration: String!) {
    listingDistribution(listingId: $listingId, duration: $duration) {
      listingId
      filter
      dateRange {
        startDate
        endDate
      }
      impressions {
        distribution {
          date
          listingType
          total
          isPeak
          isUpgrade
          isDowngrade
        }
      }
      pageViews {
        distribution {
          date
          listingType
          total
          isPeak
          isUpgrade
          isDowngrade
        }
      }
      enquiries {
        distribution {
          date
          listingType
          total
          isPeak
          isUpgrade
          isDowngrade
        }
      }
    }
  }
`;

export const MARKET_COMPARISON_QUERY = gql`
  query marketComparison($listingId: ID!, $duration: String!) {
    marketComparison(listingId: $listingId, duration: $duration) {
      listingId
      filter
      propertyTypeCode
      hasBuiltUp
      hasLand
      areaRange {
        builtUp {
          low
          high
          measurementCode
        }
        land {
          low
          high
          measurementCode
        }
      }
      impressions {
        builtUp {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
        land {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
      }
      pageViews {
        builtUp {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
        land {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
      }
      enquiries {
        builtUp {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
        land {
          self
          district
          estate
          districtPercentage {
            text
            isPositive
          }
          estatePercentage {
            text
            isPositive
          }
        }
      }
    }
  }
`;

export const PERFORMANCE_AND_MARKET_COMPARISON_QUERY = gql`
  query performanceAndMarketComparison(
    $listingIds: String!
    $duration: String!
  ) {
    performanceAndMarketComparison(
      listingIds: $listingIds
      duration: $duration
    ) {
      data {
        listingId
        pageViews
        enquiries
        impressions {
          total
          builtUp {
            districtPercentage {
              text
              isPositive
            }
          }
          land {
            districtPercentage {
              text
              isPositive
            }
          }
        }
      }
    }
  }
`;

export const LISTING_QUERY_FRAGMENT = gql`
  fragment ListingQueryFragment on Listing {
    ...BaseListingInfoFragment
    auctionDate
    postedDate
    images {
      sourceCode
      caption {
        ...MultiLangTextFields
      }
    }
    floorPlans {
      sourceCode
      caption {
        ...MultiLangTextFields
      }
    }
    photo360s {
      sourceCode
      caption {
        ...MultiLangTextFields
      }
    }
    extension {
      isPropertyNoOptional
      isCoAgency
      listingExclusivity {
        ...ReferenceFields
      }
      saleableAreaDimension {
        width
        length
      }
      grossAreaDimension {
        width
        length
      }
      occupancies {
        ...ReferenceFields
      }
      titleType {
        ...ReferenceFields
      }
      landTitleType {
        ...ReferenceFields
      }
      availableDate
      unexpiredLeaseYear
      airConditioner {
        ...ReferenceFields
      }
      carPark {
        ...ReferenceFields
      }
      completionDate
      lift
      ceilingHeight
      allowImport
      isBumiLot
      legacyLandArea
      isMigrationUpdateRequired
      isWithinMigrationGracePeriod
      isLiveTourAvailable
      maximumTenant
      allowedTenantGender {
        ...ReferenceFields
      }
      isOwnerStaying
      cookingRule {
        ...ReferenceFields
      }
      isPetAllowed
      isWifiIncluded
      isUtilityIncluded
      isVisitorAllowed
    }
    config {
      createdBrand
      createdVersion
    }
    location {
      agentoolsHideUnitFloor
    }
    isAuction
    lister {
      id
      firstName {
        ...MultiLangTextFields
      }
      lastName {
        ...MultiLangTextFields
      }
      fullName {
        ...MultiLangTextFields
      }
      description {
        ...MultiLangTextFields
      }
      email
      avatar
      organisationId
      license {
        number
        typeCode
      }
      organisation {
        id
        logo
        name {
          en_GB
          zh_HK
          zh_CN
        }
      }
      contact {
        primaryPhone {
          isWhatsApp
          number
          phoneCodeReference {
            description
          }
        }
        secondaryPhone {
          isWhatsApp
          number
          phoneCodeReference {
            description
          }
        }
      }
    }
    maintenanceFee {
      ...PriceFields
    }
    organisation {
      id
    }
    propertyCategoryType {
      ...ReferenceFields
    }
    propertyGroupType {
      ...ReferenceFields
    }
    representationLister {
      id
      firstName {
        ...MultiLangTextFields
      }
      lastName {
        ...MultiLangTextFields
      }
      fullName {
        ...MultiLangTextFields
      }
      description {
        ...MultiLangTextFields
      }
      email
      avatar
      organisationId
    }
    storey {
      ...ReferenceFields
    }
    storeyCode
    tenure {
      ...ReferenceFields
    }
    unitType {
      ...ReferenceFields
    }
    pricePerMeasurementCode {
      salePrice {
        grossArea {
          ...PriceFields
        }
        saleableArea {
          ...PriceFields
        }
      }
      rentPrice {
        grossArea {
          ...PriceFields
        }
        saleableArea {
          ...PriceFields
        }
      }
    }
    isDegraded
    isAutoUpgrade
    partnerRefId
  }
  ${BASE_LISTING_INFO_FRAGMENT}
`;

export const REFERENCES_FRAGMENT = gql`
  fragment referencesFragment on References {
    supported360VideoDomain {
      id
      code
      remark
      description
    }
    channels {
      id
      code
      description
      label
    }
    propertyTypes {
      id
      code
      description
      label
      parent {
        id
        code
        description
      }
      children {
        id
        code
        description
        label
      }
    }
    bedrooms {
      id
      code
      description
    }
    propertyLayouts {
      id
      code
      description
    }
    floorZones {
      id
      code
      description
    }
    views {
      id
      code
      description
    }
    propertyDirections {
      id
      code
      description
    }
    conditions {
      id
      code
      description
    }
    unitFeatures {
      id
      code
      description
    }
    unitTypes {
      id
      code
      description
      label
    }
    buildingFacilities {
      id
      code
      description
    }
    imageCategories {
      id
      code
      description
    }
    phoneTypes {
      id
      code
      description
    }
    richMediaTypes {
      id
      code
      description
    }
    listingStatus {
      id
      code
      description
      label
    }
    listingExclusivities {
      id
      code
      description
      label
    }
    propertyCategoryTypes {
      id
      code
      description
      label
      children {
        id
        code
        description
        label
        parent {
          id
          code
          description
          label
        }
        children {
          id
          code
          description
          label
          parent {
            id
            code
            description
            label
          }
        }
      }
    }
    propertyGroupTypes {
      id
      code
      description
      label
      parent {
        id
        code
        description
      }
      children {
        id
        code
        description
      }
    }
    storeys {
      id
      code
      description
      label
    }
    measurements {
      id
      code
      description
      label
    }
    propertyDirections {
      id
      code
      description
      label
    }
    conditions {
      id
      code
      description
      label
    }
    tenures {
      id
      code
      description
      label
    }
    titleTypes {
      id
      code
      description
      label
    }
    landTitleTypes {
      id
      code
      description
      label
    }
    occupancies {
      id
      code
      description
      label
    }
    airConditioners {
      id
      code
      description
      label
    }
    carParks {
      id
      code
      description
      label
    }
    amenities {
      id
      code
      description
      label
    }
  }
`;

export const QUALITY_FRAGMENT = gql`
  fragment qualityFragment on Quality {
    totalCategories
    totalCategoriesUnlocked
    missingFields
  }
`;

export const LISTING_PERMISSION_FRAGMENT = gql`
  fragment listingPermissionFragment on Listing {
    requestorListingPermission {
      canDuplicateListing
      canEditListing
      canEditLister
      canEditRepresentationLister
      canReAdvertiseListing
      canPublishListing
      canUpgradeListing
      canModifyAutoUpgrade
      canOfflineListing
      canDeleteListing
      canDowngradeListing
      canModifyAutoUpgrade
      canDegradeListing
      canExtendListing
      canEditLocationUnit
      canEditLocationFloor
      canEditLocationLevel1Id
      canEditLocationLevel2Id
      canEditLocationLevel3Id
      canEditLocationLevel3Text
      canEditLocationLevel4Text
      canEditLocationLevel5Text
      canEditLocationLevel5Id
      canEditLocationAddress
      canEditLocationPostalCode
      canEditLocationCoordinate
      canEditLocationHasNoTownship
    }
  }
`;

export const LISTING_PDF = gql`
  query generateListingPdf($listingId: ID!) {
    generateListingPdf(listingId: $listingId) {
      url
    }
  }
`;

export const LISTING_QUERY = gql`
  query listingQuery($id: ID!, $shouldExtendsFields: Boolean = false) {
    listing(id: $id) {
      ...ListingQueryFragment
      ...listingPermissionFragment
    }
  }
  ${LISTING_QUERY_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
`;

export const LISTINGS_VISIBILITY_QUERY = gql`
  query visibilitiesQuery {
    visibilities {
      listingId
      visibilityPage {
        code
        description
      }
    }
  }
`;

export const LISTINGS_DISCOUNT_QUERY = gql`
  query listingsDiscountQuery($listings: [ListingDiscountReq!]) {
    listingDiscounts(listings: $listings) {
      discounts {
        listingId
        discount
      }
    }
  }
`;

export const LEAD_COUNT_BY_LISTINGS = gql`
  query leadCountByListingsQuery($listingIds: [String]) {
    leadCountByListings(listingIds: $listingIds) {
      data {
        listingId
        total
        unread
      }
    }
  }
`;

export const REFERENCES_QUERY = gql`
  query referencesQuery {
    references {
      ...referencesFragment
    }
  }
  ${REFERENCES_FRAGMENT}
`;

export const DUPLICATE_LISTING = gql`
  mutation duplicateListing($input: DuplicateListingInput!) {
    duplicateListing(input: $input) {
      listing {
        id
        postedDate
      }
    }
  }
`;

export const PRE_VALIDATE_LISTING_MUTATION = gql`
  mutation preValidateListingMutation($input: PreValidateListingInput!) {
    preValidateListing(input: $input) {
      errors
    }
  }
`;

export const ADD_LISTING_MUTATION = gql`
  mutation addListingMutation($input: AddListingInput!) {
    addListing(input: $input) {
      listing {
        id
        channel {
          id
          code
          description
          label
        }
        propertyType {
          id
          code
          description
          label
        }
        lister {
          id
          firstName {
            en_GB
            zh_HK
            zh_CN
          }
          lastName {
            en_GB
            zh_HK
            zh_CN
          }
          fullName {
            en_GB
            zh_HK
            zh_CN
          }
          email
        }
        representationLister {
          id
          firstName {
            en_GB
            zh_HK
            zh_CN
          }
          lastName {
            en_GB
            zh_HK
            zh_CN
          }
          fullName {
            en_GB
            zh_HK
            zh_CN
          }
          email
        }
        listingRefNo
        extension {
          isCoAgency
          listingExclusivity {
            id
            code
            description
            label
          }
        }
        propertyCategoryType {
          id
          code
          description
          label
        }
        isAuction
        auctionDate
        postedDate
      }
    }
  }
`;

export const UPDATE_LISTING_MUTATION = gql`
  mutation updateListingInfo(
    $input: UpdateListingInput!
    $shouldExtendsFields: Boolean = false
  ) {
    updateListing(input: $input) {
      listing {
        ...ListingQueryFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
`;
export const PUBLISH_LISTING = gql`
  mutation publishListingInfo(
    $input: PublishListingInput!
    $shouldExtendsFields: Boolean = false
  ) {
    publishListing(input: $input) {
      listings {
        ...ListingQueryFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
`;

export const UPGRADE_LISTING = gql`
  mutation UpgradeListing(
    $input: UpgradeListingInput!
    $shouldExtendsFields: Boolean = false
  ) {
    upgradeListing(input: $input) {
      listing {
        ...ListingQueryFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
`;

export const DOWNGRADE_LISTING = gql`
  mutation DowngradeListing(
    $input: DowngradeListingInput!
    $shouldExtendsFields: Boolean = false
  ) {
    downgradeListing(input: $input) {
      listing {
        ...ListingQueryFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
`;

export const DEGRADE_LISTING = gql`
  mutation degradeListing($input: DegradeListingInput!) {
    degradeListing(input: $input) {
      listing {
        id
        isDegraded
      }
    }
  }
`;

export const REVERT_DEGRADE_LISTING = gql`
  mutation revertDegradeListing($input: RevertDegradeListingInput!) {
    revertDegradeListing(input: $input) {
      listing {
        id
        isDegraded
      }
    }
  }
`;

export const EXPORT_LISTING = gql`
  mutation ExportListings($input: ExportListingsInput!) {
    exportListings(input: $input) {
      message
    }
  }
`;

export const EXTEND_LISTING = gql`
  mutation extendListing(
    $input: ExtendListingInput!
    $shouldExtendsFields: Boolean = true
  ) {
    extendListing(input: $input) {
      listing {
        ...ListingQueryFragment
        ...listingPermissionFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
`;

export const CANCEL_AUTO_UPGRADE = gql`
  mutation cancelAutoUpgrade($input: CancelAutoUpgradeInput!) {
    cancelAutoUpgrade(input: $input) {
      listing {
        id
        isAutoUpgrade
      }
    }
  }
`;

export const QUALITY_QUERY = gql`
  query qualityQuery($id: ID) {
    quality(id: $id) {
      ...qualityFragment
    }
  }
  ${QUALITY_FRAGMENT}
`;

export const EDIT_LISTING_QUERY = gql`
  query editListingQuery(
    $id: ID!
    $shouldExtendsFields: Boolean = false
    $includeQuality: Boolean = false
  ) {
    listing(id: $id) {
      ...ListingQueryFragment
      ...listingPermissionFragment
    }
    references {
      ...referencesFragment
    }
    quality(id: $id) @include(if: $includeQuality) {
      ...qualityFragment
    }
  }
  ${REFERENCES_FRAGMENT}
  ${LISTING_QUERY_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
  ${QUALITY_FRAGMENT}
`;

export const LISTING_OVERVIEW_QUERY = gql`
  query listingOverviewQuery(
    $id: ID!
    $shouldExtendsFields: Boolean = false
    $includeReAdvertiseJob: Boolean
    $includeQuality: Boolean = false
  ) {
    listing(id: $id, includeReAdvertiseJob: $includeReAdvertiseJob) {
      ...ListingQueryFragment
      ...listingPermissionFragment
      actionDate
      postedDate
      updatedDate
      createdBy {
        id
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
      updatedBy {
        id
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
      ownership {
        organisation {
          id
          name {
            en_GB
            zh_HK
            zh_CN
          }
        }
        user {
          id
          fullName {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
      shareLink
      isDegraded
      isAutoUpgrade
      reAdvertiseJobs {
        schedulerDateTime
      }
    }
    references {
      ...referencesFragment
    }
    quality(id: $id) @include(if: $includeQuality) {
      ...qualityFragment
    }
  }
  ${REFERENCES_FRAGMENT}
  ${LISTING_QUERY_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
  ${QUALITY_FRAGMENT}
`;

export const OFFLINE_LISTING = gql`
  mutation offlineListing($input: OfflineListingInput!) {
    offlineListing(input: $input) {
      listing {
        id
        status {
          id
          code
          description
          label
        }
        actionDate
        updatedDate
        postedDate
        isAutoUpgrade
      }
    }
  }
`;

export const LOCATION_QUERY = gql`
  query locationPlaces(
    $keyword: String
    $level1Id: String
    $level2Id: String
    $level3Id: String
    $level5Id: String
    $q: String
    $shouldExtendsFields: Boolean = false
  ) {
    places(
      searchAddress: true
      keyword: $keyword
      level1Id: $level1Id
      level2Id: $level2Id
      level3Id: $level3Id
      level5Id: $level5Id
      q: $q
      limit: 50
    ) {
      data {
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level3 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level4 @include(if: $shouldExtendsFields) {
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level5 @include(if: $shouldExtendsFields) {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        propertyType @include(if: $shouldExtendsFields) {
          id
          code
          description
        }
        propertyGroupType @include(if: $shouldExtendsFields) {
          id
          code
          description
        }
        postCode @include(if: $shouldExtendsFields)
        address {
          en_GB
          zh_HK
          zh_CN
        }
      }
    }
  }
`;

export const LOCATION_V2_QUERY = gql`
  query locationPlacesV2(
    $q: String
    $keyword: String
    $nanoIds: String
    $parentNanoId: String
    $parentLevel: String
    $propertyCategoryCode: Int
    $propertyTypeCode: Int
    $legacyIds: String
    $page: String
    $limit: Int = 100
    $shouldExtendsFields: Boolean = false
    $includeBuildingFacility: Boolean = false
    $includeOtherOption: Boolean
  ) {
    placesV2(
      q: $q
      keyword: $keyword
      nanoIds: $nanoIds
      parentNanoId: $parentNanoId
      parentLevel: $parentLevel
      propertyCategoryCode: $propertyCategoryCode
      propertyTypeCode: $propertyTypeCode
      legacyIds: $legacyIds
      page: $page
      limit: $limit
      includeOtherOption: $includeOtherOption
    ) {
      data {
        level1 {
          nanoId
          text {
            en_GB
          }
        }
        level2 {
          nanoId
          text {
            en_GB
          }
        }
        level3 {
          nanoId
          text {
            en_GB
          }
        }
        level4 {
          text {
            en_GB
          }
        }
        level5 {
          nanoId
          text {
            en_GB
          }
        }
        postCode
        address {
          en_GB
        }
        propertyType @include(if: $shouldExtendsFields) {
          code
          description
        }
        propertyGroupType @include(if: $shouldExtendsFields) {
          code
          description
        }
        propertyCategoryType @include(if: $shouldExtendsFields) {
          code
          description
        }
        hasTownship
        latitude
        longitude
        buildingFacilities @include(if: $includeBuildingFacility) {
          code
          description
        }
      }
    }
  }
`;

export const AUTOCOMPLETE_QUERY = gql`
  query autocomplete(
    $keyword: String!
    $levels: String
    $level1Text: String
    $level2Text: String
    $level3Text: String
    $level1NanoIds: String
    $level2NanoIds: String
    $level3NanoIds: String
    $level5NanoIds: String
    $propertyTypeCode: Int
    $resolveLocation: Boolean = false
    $includeBuildingFacility: Boolean = false
    $singleRequest: Boolean = true
    $showAllProjects: Boolean = false
  ) {
    autocomplete(
      keyword: $keyword
      levels: $levels
      level1Text: $level1Text
      level2Text: $level2Text
      level3Text: $level3Text
      level1NanoIds: $level1NanoIds
      level2NanoIds: $level2NanoIds
      level3NanoIds: $level3NanoIds
      level5NanoIds: $level5NanoIds
      propertyTypeCode: $propertyTypeCode
      resolveLocation: $resolveLocation
      limit: 15
      singleRequest: $singleRequest
      showAllProjects: $showAllProjects
    ) {
      data {
        level1 {
          nanoId
          text {
            en_GB
          }
        }
        level2 {
          nanoId
          text {
            en_GB
          }
        }
        level3 {
          nanoId
          text {
            en_GB
          }
        }
        level4 {
          text {
            en_GB
          }
        }
        level5 {
          nanoId
          text {
            en_GB
          }
        }
        postCode @include(if: $resolveLocation)
        address @include(if: $resolveLocation) {
          en_GB
        }
        propertyType {
          code
          description
        }
        propertyGroupType @include(if: $resolveLocation) {
          code
          description
        }
        propertyCategoryType @include(if: $resolveLocation) {
          code
          description
        }
        hasTownship @include(if: $resolveLocation)
        latitude @include(if: $resolveLocation)
        longitude @include(if: $resolveLocation)
        buildingFacilities @include(if: $includeBuildingFacility) {
          code
          description
        }
      }
    }
  }
`;

export const PROPERTY_REFERENCE_QUERY = gql`
  query propertyReferences {
    references {
      bedrooms {
        id
        code
        description
      }
      propertyTypes {
        id
        code
        description
        label
        children {
          id
          code
          description
        }
        parent {
          id
          code
          description
          parent {
            id
            code
            description
          }
        }
      }
      channels {
        id
        code
        description
        label
      }
      listingStatus {
        id
        code
        description
        label
      }
      listingProducts {
        id
        code
        description
        label
      }
      qualityIssueTypes {
        id
        code
        description
        label
      }
      qualityIssueImage {
        id
        code
        description
        label
      }
      qualityIssueAddress {
        id
        code
        description
        label
      }
      agenToolListingTypes {
        id
        code
        description
        label
      }
      agenToolPropertyTypes {
        id
        code
        description
        label
      }
      agenToolSourceTypes {
        id
        code
        description
        label
      }
      propertyCategoryTypes {
        id
        code
        description
        label
        children {
          id
          code
          description
          label
          parent {
            id
            code
            description
            label
          }
          children {
            id
            code
            description
            label
            parent {
              id
              code
              description
            }
          }
        }
      }
    }
  }
`;

export const PROPERTY_LISTINGS_QUERY_NAME = "listingsQuery";
export const PROPERTY_LISTINGS_QUERY = gql`
  query listingsQuery(
    $listingId: ID
    $listerId: Int
    $channelCode: Int
    $locationLevel1: Int
    $locationLevel2: Int
    $locationLevel3: Int
    $locationLevel5: Int
    $locationLevel1NanoId: String
    $locationLevel2NanoId: String
    $locationLevel3NanoId: String
    $locationLevel5NanoId: String
    $locationLevel3Text: String
    $locationLevel4Text: String
    $locationLevel5Text: String
    $statusCode: Int
    $page: String
    $searchText: String
    $limit: Int
    $sortBy: String
    $productCode: Int
    $isDegraded: Boolean
    $organisationId: Int
    $propertyTypeCode: Int
    $isExcludeChild: Boolean
    $quality: Boolean
    $listingListerType: String
    $shouldExtendsFields: Boolean = false
    $propertyCategoryTypeCode: Int
    $unitTypeCode: Int
    $fromPrice: Float
    $toPrice: Float
    $isAutoUpgrade: Boolean
    $includeReAdvertiseJob: Boolean
    $propertyTypeCodes: [Int]
    $propertyGroupTypeCode: Int
    $isRequiredLocationUpdate: Boolean
    $includeSummary: Boolean = false
    $isAuction: Boolean
  ) {
    listings(
      listingId: $listingId
      channelCode: $channelCode
      listerId: $listerId
      locationLevel1: $locationLevel1
      locationLevel2: $locationLevel2
      locationLevel3: $locationLevel3
      locationLevel5: $locationLevel5
      locationLevel1NanoId: $locationLevel1NanoId
      locationLevel2NanoId: $locationLevel2NanoId
      locationLevel3NanoId: $locationLevel3NanoId
      locationLevel5NanoId: $locationLevel5NanoId
      locationLevel3Text: $locationLevel3Text
      locationLevel4Text: $locationLevel4Text
      locationLevel5Text: $locationLevel5Text
      statusCode: $statusCode
      propertyTypeCode: $propertyTypeCode
      quality: $quality
      limit: $limit
      page: $page
      searchText: $searchText
      sortBy: $sortBy
      productCode: $productCode
      isDegraded: $isDegraded
      organisationId: $organisationId
      isExcludeChild: $isExcludeChild
      listingListerType: $listingListerType
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      unitTypeCode: $unitTypeCode
      fromPrice: $fromPrice
      toPrice: $toPrice
      isAutoUpgrade: $isAutoUpgrade
      includeReAdvertiseJob: $includeReAdvertiseJob
      propertyTypeCodes: $propertyTypeCodes
      propertyGroupTypeCode: $propertyGroupTypeCode
      isRequiredLocationUpdate: $isRequiredLocationUpdate
      isAuction: $isAuction
    ) {
      pagination {
        nextPageToken
        limit
        page
        total
      }
      totalPerStatus {
        status {
          id
          code
          description
        }
        total
      }
      summary @include(if: $includeSummary) {
        isRequiredLocationUpdate
      }
      data {
        ...BaseListingInfoFragment
        actionDate
        createdDate
        lister {
          id
          fullName {
            ...MultiLangTextFields
          }
        }
        postedDate
        quality
        isDegraded
        isAutoUpgrade
        isAuction
        auctionDate
        reAdvertiseJobs {
          schedulerDateTime
        }
        partnerRefId
        discount {
          discount
        }
        ...listingPermissionFragment
      }
    }
  }
  ${BASE_LISTING_INFO_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
`;

export const AGENCY_REFERENCES_QUERY = gql`
  query agencyReferencesQuery {
    references {
      phoneCodes {
        id
        code
        description
      }
      organisationTypes {
        id
        code
        description
      }
    }
  }
`;

export const AGENCY_QUERY = gql`
  query agencyQuery($includeAgency: Boolean = true) {
    agencies(sortBy: "nameAsc") {
      id
      name {
        en_GB
        zh_HK
        zh_CN
      }
      allocationOn
      branches(includeAgency: $includeAgency) {
        id
        name {
          en_GB
          zh_HK
          zh_CN
        }
        allocationOn
      }
    }
  }
`;

export const USER_BY_ID = gql`
  query singleUserQuery($id: ID!) {
    getUserById(id: $id) {
      id
      organisationId
      firstName {
        en_GB
        zh_HK
        zh_CN
      }
      lastName {
        en_GB
        zh_HK
        zh_CN
      }
      fullName {
        en_GB
        zh_HK
        zh_CN
      }
      email
    }
  }
`;
export const AGENCY_CREATION = gql`
  mutation addOrganization($input: AddOrganisationInput!) {
    addOrganisation(input: $input) {
      organisation {
        id
        spotReaId
        name {
          en_GB
          zh_CN
          zh_HK
        }
        parentId
        marketOrgId
        market
        contact {
          primaryPhone {
            number
            phoneCode {
              id
              code
              description
            }
          }
        }
        location {
          level1 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          address {
            en_GB
            zh_CN
            zh_HK
          }
        }
        email
        website
        logo
        color
        createdDate
        updatedDate
        hasCustomName
        customName {
          en_GB
          zh_HK
          zh_CN
        }
      }
    }
  }
`;

export const SG_ORGANIZATIONS_QUERY = gql`
  query organizationSGQuery(
    $organisationTypeCode: Int
    $searchText: String
    $limit: Int
    $page: String
  ) {
    organisations(
      organisationTypeCode: $organisationTypeCode
      searchText: $searchText
      limit: $limit
      page: $page
    ) {
      pagination {
        nextPageToken
        limit
      }
      data {
        id
        location {
          level1 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
        }
        logo
        name {
          en_GB
          zh_CN
          zh_HK
        }
        spotReaId
        isAgency
        isBranch
        parent {
          id
          name {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const ORGANIZATIONS_QUERY = gql`
  query organizationQuery(
    $organisationTypeCode: Int
    $searchText: String
    $limit: Int
    $page: String
    $shouldUseNanoId: Boolean = false
  ) {
    organisations(
      organisationTypeCode: $organisationTypeCode
      searchText: $searchText
      limit: $limit
      page: $page
    ) {
      pagination {
        nextPageToken
        limit
      }
      data {
        id
        location {
          level1 {
            id @skip(if: $shouldUseNanoId)
            nanoId @include(if: $shouldUseNanoId)
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          level2 {
            id @skip(if: $shouldUseNanoId)
            nanoId @include(if: $shouldUseNanoId)
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
        }
        logo
        name {
          en_GB
          zh_CN
          zh_HK
        }
        spotReaId
        parent {
          id
          name {
            en_GB
            zh_HK
            zh_CN
          }
        }
        isAgency
        isBranch
      }
    }
  }
`;
export const UPDATE_ORGANIZATION = gql`
  mutation updateOrganization($input: UpdateOrganisationInput!) {
    updateOrganisation(input: $input) {
      organisation {
        id
        spotReaId
        name {
          en_GB
          zh_CN
          zh_HK
        }
        parentId
        marketOrgId
        market
        contact {
          primaryPhone {
            number
            phoneCode {
              id
              code
              description
            }
          }
        }
        location {
          level1 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_CN
              zh_HK
            }
          }
          address {
            en_GB
            zh_CN
            zh_HK
          }
        }
        email
        website
        logo
        color
        createdDate
        updatedDate
        allocationOn
      }
    }
  }
`;

export const QUERY_ORGANIZATION = gql`
  query organization($Id: ID!) {
    organisation(id: $Id) {
      id
      isAgency
      isBranch
      spotReaId
      name {
        en_GB
        zh_CN
        zh_HK
      }
      parentId
      licenseNo
      marketOrgId
      market
      contact {
        primaryPhone {
          number
          phoneCode {
            id
            code
            description
          }
        }
      }
      location {
        level1 {
          id
          nanoId
          text {
            en_GB
            zh_CN
            zh_HK
          }
        }
        level2 {
          id
          nanoId
          text {
            en_GB
            zh_CN
            zh_HK
          }
        }
        address {
          en_GB
          zh_CN
          zh_HK
        }
      }
      email
      website
      logo
      profileLogo
      color
      createdDate
      updatedDate
      allocationOn
      hasContract
      hasCustomName
      customName {
        en_GB
        zh_HK
        zh_CN
      }
    }
  }
`;

export const QUOTES_FRAGMENT = gql`
  fragment quotesFragment on Quote {
    quoteId
    reaId
    title
    description
    listingProduct {
      id
      code
      label
    }
    productDuration
    remaining
    productCode
    disabled
    cost
    originalCost
    discountPercentage
    isPromo
    promoBase {
      productDuration
    }
  }
`;

export const READVERTISE_AD_CREDITS_QUERY = gql`
  query ReAdvertiseAdCreditsQuery {
    quotes(productTypeCode: 2) {
      quoteId
      cost
      title
      remaining
      productCode
      propertyCategoryCode
      channelCode
      productCode
    }
  }
`;

export const UPGRADE_CREDITS_QUERY = gql`
  query UpgradeCreditsQuery {
    quotes(productTypeCode: 1) {
      cost
      remaining
      channelCode
      productCode
      propertyCategoryCode
      locationLevel1Id
      productDuration
      user {
        id
      }
      organisation {
        id
      }
      isPromo
      promoBase {
        productDuration
      }
    }
  }
`;

export const LISTING_REMAINING_QUERY = gql`
  query ListingRemainingQuery {
    listings(statusCode: 2, page: "1", limit: 1) {
      data {
        upgrade {
          ...quotesFragment
        }
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;
export const EXTEND_LISTING_QUOTES_QUERY = gql`
  query ExtendListingQuotesQuery($ids: [ID!], $limit: Int = 100) {
    listings(listingIds: $ids, statusCode: 2, limit: $limit) {
      data {
        id
        upgrade {
          ...quotesFragment
        }
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;
export const LISTINGS_OF_CACHE_QUERY = gql`
  query ListingsOfCacheQuery(
    $ids: [ID!]!
    $shouldExtendsFields: Boolean = false
  ) {
    listingsOfCache(ids: $ids) @client {
      ...BaseListingInfoFragment
      ...listingPermissionFragment
    }
  }
  ${BASE_LISTING_INFO_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
`;

export const LISTING_OF_CACHE_QUERY = gql`
  query ListingOfCacheQuery($id: ID!, $shouldExtendsFields: Boolean = false) {
    listingOfCache(id: $id) @client {
      ...BaseListingInfoFragment
      ...listingPermissionFragment
    }
  }
  ${BASE_LISTING_INFO_FRAGMENT}
  ${LISTING_PERMISSION_FRAGMENT}
`;

export const READVERTISE_LISTING = gql`
  mutation ReAdvertiseListing($input: ReAdvertiseListingInput!) {
    reAdvertiseListing(input: $input) {
      listing {
        id
        postedDate
      }
    }
  }
`;

export const QUOTES_QUERY = gql`
  query QuotesQuery($id: ID!, $isSale: Boolean!) {
    sale: listing(id: $id) @connection(key: quotes_sale) @include(if: $isSale) {
      quotes {
        ...quotesFragment
      }
    }
    rent: listing(id: $id) @connection(key: quotes_rent) @skip(if: $isSale) {
      quotes {
        ...quotesFragment
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;

export const LISTING_QUOTES_QUERY = gql`
  query ListingQuoteQuery($id: ID!) {
    listing(id: $id) {
      id
      quotes {
        ...quotesFragment
      }
      agentNetQuotes {
        cost
        productCode
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;

export const LISTING_QUOTES_QUERY_READVERTISE = gql`
  query ListingQuoteQueryReAdvertise($ids: [ID!]) {
    listingQuotes(ids: $ids) {
      reAdvertise {
        quoteId
        listingId
        cost
        productCode
        remaining
        title
      }
    }
  }
`;

export const LISTING_POSITION_QUERY = gql`
  query listingPositionQuery($listingId: ID!, $channelCode: Int!) {
    listingPosition(listingId: $listingId, channelCode: $channelCode) {
      inSearchRange
      position
    }
  }
`;

export const LISTING_UPGRADE_QUERY = gql`
  query listingUpgradeQuery($id: ID!) {
    listing(id: $id) {
      id
      channel {
        id
        code
      }
      productDuration
      upgrade {
        ...quotesFragment
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;

export const LISTING_UPGRADE_QUOTES_QUERY = gql`
  query listingUpgradeQuotesQuery($id: ID!) {
    listing(id: $id) {
      upgrade {
        ...quotesFragment
      }
    }
  }
  ${QUOTES_FRAGMENT}
`;

export const ENQUIRIES_QUERY = gql`
  query enquiriesQuery($Id: ID!) {
    organisation(id: $Id) {
      ccEmails {
        email
      }
    }
  }
`;

export const UPDATE_ENQUIRIES = gql`
  mutation updateEnquiries($input: UpdateOrganisationEnquiriesInput!) {
    updateOrganisationEnquiries(input: $input) {
      organisation {
        id
        ccEmails {
          email
        }
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_LISTING_QUERY = gql`
  query performanceByListingsQuery(
    $listerId: ID
    $organisationId: ID
    $channelCode: Int
    $propertyCategoryTypeCode: Int
    $timeline: String
    $statusCode: Int
    $productCode: Int
    $quality: Boolean
    $locationLevel1: Int
    $locationLevel2: Int
    $locationLevel3: Int
    $locationLevel5: Int
    $locationLevel1NanoId: String
    $locationLevel2NanoId: String
    $locationLevel3NanoId: String
    $locationLevel5NanoId: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByListings(
      listerId: $listerId
      organisationId: $organisationId
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      channelCode: $channelCode
      timeline: $timeline
      statusCode: $statusCode
      productCode: $productCode
      quality: $quality
      locationLevel1: $locationLevel1
      locationLevel2: $locationLevel2
      locationLevel3: $locationLevel3
      locationLevel5: $locationLevel5
      locationLevel1NanoId: $locationLevel1NanoId
      locationLevel2NanoId: $locationLevel2NanoId
      locationLevel3NanoId: $locationLevel3NanoId
      locationLevel5NanoId: $locationLevel5NanoId
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        id
        listingRefNo
        propertyNo
        status
        channel
        locationLevel1
        locationLevel2
        locationLevel3
        locationLevel5
        price
        product
        quality
        daysOnSite
        impressions
        pageViews
        enquiries
        listerName
        marketComparison {
          impressions {
            builtUp {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }

            land {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }
          }
          pageViews {
            builtUp {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }

            land {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }
          }
          enquiries {
            builtUp {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }

            land {
              district
              estate
              self
              districtPercentage {
                text
                isPositive
              }
              estatePercentage {
                text
                isPositive
              }
            }
          }
        }
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
      dataSummary {
        totalPageViews
        totalEnquiries
        totalImpressions
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_LOCATION2_QUERY = gql`
  query performanceByLocationLevel2Query(
    $listerId: ID
    $organisationId: ID
    $propertyCategoryTypeCode: Int
    $channelCode: Int
    $timeline: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByLocationLevel2(
      listerId: $listerId
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      organisationId: $organisationId
      channelCode: $channelCode
      timeline: $timeline
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        locationLevel1
        locationLevel1Id
        locationLevel1NanoId
        locationLevel2
        locationLevel2Id
        locationLevel2NanoId
        totalListings
        impressions
        pageViews
        enquiries
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_LOCATION3_QUERY = gql`
  query performanceByLocationLevel3Query(
    $listerId: ID
    $organisationId: ID
    $channelCode: Int
    $timeline: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByLocationLevel3(
      listerId: $listerId
      organisationId: $organisationId
      channelCode: $channelCode
      timeline: $timeline
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        locationLevel1
        locationLevel2
        locationLevel3
        locationLevel3Id
        locationLevel2Id
        locationLevel1NanoId
        locationLevel2NanoId
        locationLevel3NanoId
        totalListings
        impressions
        pageViews
        enquiries
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
      dataSummary {
        totalPageViews
        totalEnquiries
        totalImpressions
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_LOCATION5_QUERY = gql`
  query performanceByLocationLevel5Query(
    $listerId: ID
    $organisationId: ID
    $propertyCategoryTypeCode: Int
    $channelCode: Int
    $timeline: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByLocationLevel5(
      listerId: $listerId
      organisationId: $organisationId
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      channelCode: $channelCode
      timeline: $timeline
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        locationLevel1
        locationLevel1Id
        locationLevel1NanoId
        locationLevel2
        locationLevel2Id
        locationLevel2NanoId
        locationLevel3NanoId
        locationLevel3
        locationLevel5
        locationLevel5Id
        locationLevel5NanoId
        totalListings
        impressions
        pageViews
        enquiries
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_USER_QUERY = gql`
  query performanceByUsersQuery(
    $listerId: ID
    $organisationId: ID
    $channelCode: Int
    $timeline: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByUsers(
      listerId: $listerId
      organisationId: $organisationId
      channelCode: $channelCode
      timeline: $timeline
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        id
        organisationName
        name
        totalListings
        impressions
        pageViews
        enquiries
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const DISTRICT_FILTER_QUERY = gql`
  query districtFilterQuery($level3Id: String) {
    places(level3Id: $level3Id, q: "level2", limit: 100) {
      data {
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const ESTATE_FILTER_QUERY = gql`
  query estateFilterQuery($keyword: String) {
    districts: places(
      keyword: $keyword
      q: "level2"
      limit: 100
      includePlaceId: true
    ) {
      data {
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
    estates: places(
      keyword: $keyword
      q: "level3"
      limit: 50
      includePlaceId: true
    ) {
      data {
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level3 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const PROPERTY_REPORTINGS_ORGANISATION_QUERY = gql`
  query performanceByOrganisationQuery(
    $listerId: ID
    $organisationId: ID
    $channelCode: Int
    $timeline: String
    $sortBy: String
    $limit: Int
    $page: Int
    $isExcludeChild: Boolean
  ) {
    performanceByOrganisations(
      listerId: $listerId
      organisationId: $organisationId
      channelCode: $channelCode
      timeline: $timeline
      sortBy: $sortBy
      limit: $limit
      page: $page
      isExcludeChild: $isExcludeChild
    ) {
      globalOverview {
        totalListings
        totalImpressions
        totalPageViews
        totalEnquiries
        phone
        email
        whatsapp
        listingSaves
        photoViews
        totalShares
        dateFrom
        dateTo
      }
      data {
        id
        name
        totalListings
        impressions
        pageViews
        enquiries
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const CREATE_REPORT = gql`
  mutation downloadPerformanceReport($input: DownloadPerformanceReportInput!) {
    downloadPerformanceReport(input: $input) {
      statusCode
    }
  }
`;

export const CREATE_SPECIALIST_REPORT = gql`
  mutation downloadSpecialistReport {
    downloadSpecialistReport {
      statusCode
    }
  }
`;

export const ALLOCATION_LIST_QUERY = gql`
  query allocationQuery(
    $organisationId: ID!
    $allocatedOrganisationId: ID
    $allocatedUserId: ID
    $page: String
    $limit: Int
  ) {
    allocation(
      organisationId: $organisationId
      allocatedOrganisationId: $allocatedOrganisationId
      allocatedUserId: $allocatedUserId
      page: $page
      limit: $limit
    ) {
      organisationId
      organisation {
        name {
          en_GB
          zh_HK
          zh_CN
        }
      }
      reaId
      expiryDate
      overview {
        productCode
        listingProduct {
          id
          code
          description
        }
        allocated
        assigned
        nonAssigned
        used
        available
      }
      allocations {
        organisationId
        organisation {
          location {
            level1 {
              text {
                en_GB
                zh_HK
                zh_CN
              }
            }
            level2 {
              text {
                en_GB
                zh_HK
                zh_CN
              }
            }
          }
          name {
            en_GB
            zh_HK
            zh_CN
          }
        }
        userId
        user {
          avatar
          email
          fullName {
            en_GB
            zh_HK
            zh_CN
          }
        }
        reaId
        products {
          productCode
          listingProduct {
            id
            code
            description
          }
          allocated
          assigned
          nonAssigned
          used
          available
        }
      }
      pagination {
        page
        limit
        total
      }
    }
  }
`;

export const QUERY_MEMBER = gql`
  query queryMember($searchText: String, $limit: Int) {
    organizationUserSuggestion(searchText: $searchText, limit: $limit) {
      organisations {
        id
        isAgency
        location {
          level1 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          address {
            en_GB
            zh_HK
            zh_CN
          }
        }
        name {
          en_GB
          zh_HK
          zh_CN
        }
      }
      users {
        id
        organisationId
        email
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
    }
  }
`;

export const UPDATE_ALLOCATION = gql`
  mutation updateAllocation($input: UpdateAllocationInput!) {
    updateAllocation(input: $input) {
      isSuccess
    }
  }
`;

export const QUERY_USER_ALLOCATION = gql`
  query userAllocation($userId: ID!, $contractOrganisationId: ID) {
    userAllocation(
      userId: $userId
      contractOrganisationId: $contractOrganisationId
    ) {
      allocations {
        products {
          productCode
          listingProduct {
            id
            code
            description
          }
          allocated
          assigned
          nonAssigned
          used
          available
        }
      }
    }
  }
`;

export const QUERY_ORGANISATION_ALLOCATION = gql`
  query organisationAllocation(
    $organisationId: ID!
    $contractOrganisationId: ID
  ) {
    organisationAllocation(
      organisationId: $organisationId
      contractOrganisationId: $contractOrganisationId
    ) {
      allocations {
        products {
          productCode
          listingProduct {
            id
            code
            description
          }
          allocated
          assigned
          nonAssigned
          used
          available
        }
      }
    }
  }
`;

export const AGENT_QUERY = gql`
  query agentQuery(
    $keyword: String
    $organisationId: Int
    $isExcludeChild: Boolean
  ) {
    users(
      keyword: $keyword
      organisationId: $organisationId
      roleCode: 6
      limit: 50
      isExcludeChild: $isExcludeChild
    ) {
      data {
        id
        displayName
        organisationId
        avatar
        license {
          number
          licenseTypeReference {
            description
          }
        }
        firstName {
          en_GB
          zh_HK
          zh_CN
        }
        lastName {
          en_GB
          zh_HK
          zh_CN
        }
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
        email
      }
    }
  }
`;

export const AGENT_QUERY_ALL = gql`
  query agentQueryAll(
    $keyword: String
    $organisationId: Int
    $isExcludeChild: Boolean
  ) {
    users(
      keyword: $keyword
      organisationId: $organisationId
      limit: 50
      isExcludeChild: $isExcludeChild
    ) {
      data {
        id
        organisationId
        firstName {
          en_GB
          zh_HK
          zh_CN
        }
        lastName {
          en_GB
          zh_HK
          zh_CN
        }
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
        email
      }
    }
  }
`;

export const QUERY_ALLOCATION_REPORT = gql`
  query allocationHistories($organisationId: ID!) {
    allocationHistories(organisationId: $organisationId) {
      data {
        allocationDate
        allocatedFrom {
          en_GB
          zh_HK
          zh_CN
        }
        allocatedTo {
          en_GB
          zh_HK
          zh_CN
        }
        allocatedFrom {
          en_GB
          zh_HK
          zh_CN
        }
        allocatedBy {
          en_GB
          zh_HK
          zh_CN
        }
        products {
          quantity
          listingProduct {
            id
            code
            description
          }
        }
      }
    }
  }
`;

export const QUERY_QUALITY_ISSUES = gql`
  query qualityIssueListQuery(
    $listingId: ID
    $productCode: Int
    $isVerified: Boolean
    $listingUpdatedDateFrom: String
    $listingUpdatedDateTo: String
    $listingQuality: Int
    $sortBy: String
    $limit: Int
    $page: Int
  ) {
    qualityIssueList(
      listingId: $listingId
      productCode: $productCode
      isVerified: $isVerified
      listingUpdatedDateFrom: $listingUpdatedDateFrom
      listingUpdatedDateTo: $listingUpdatedDateTo
      listingQuality: $listingQuality
      sortBy: $sortBy
      limit: $limit
      page: $page
    ) {
      data {
        id
        market
        listingId
        listingUpdatedDate
        agingInSecond
        issueType
        productCode
        listingQuality
        isVerified
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const QUERY_LEAD_BY_ID = gql`
  query leadByIdQuery($id: ID!) {
    lead(id: $id) {
      id
      listingId
      channelCode
      listingDesc
      date
      enquirerName
      enquirerEmail
      enquirerPhone
      message
    }
  }
`;

export const QUERY_QUALITY_ISSUE = gql`
  query qualityIssueQuery($id: ID!) {
    qualityIssue(id: $id) {
      id
      market
      listingId
      listingUpdatedDate
      issueType
      productCode
      isVerified
      createdDate
      verifiedDate
      isOutdated
      propertyTypeCode
      verifiedBy {
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
      issueData {
        totalImage
        totalImageWithIssue
        location {
          address {
            en_GB
            zh_HK
            zh_CN
          }
          level1 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level3 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          floor
          issueVerified
          note
          unit
        }
        images {
          issueDetected
          issueVerified
          note
          url
          path
        }
      }
    }
  }
`;

export const PATCH_QUALITYISSUEADDRESS = gql`
  mutation patchQualityIssuesLocation(
    $input: patchQualityIssuesLocationInput!
  ) {
    patchQualityIssuesLocation(input: $input) {
      id
      market
      listingId
      listingUpdatedDate
      issueType
      productCode
      isVerified
      createdDate
      verifiedDate
      isOutdated
      propertyTypeCode
      verifiedBy {
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
      issueData {
        totalImage
        totalImageWithIssue
        location {
          address {
            en_GB
            zh_HK
            zh_CN
          }
          level1 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level3 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          floor
          issueVerified
          note
          unit
        }
        images {
          issueDetected
          issueVerified
          note
          url
          path
        }
      }
    }
  }
`;

export const PATCH_QUALITYISSUEIMAGE = gql`
  mutation patchQualityIssuesImage($input: patchQualityIssuesImageInput!) {
    patchQualityIssuesImage(input: $input) {
      id
      market
      listingId
      listingUpdatedDate
      issueType
      productCode
      isVerified
      createdDate
      verifiedDate
      isOutdated
      propertyTypeCode
      verifiedBy {
        fullName {
          en_GB
          zh_HK
          zh_CN
        }
      }
      issueData {
        totalImage
        totalImageWithIssue
        location {
          address {
            en_GB
            zh_HK
            zh_CN
          }
          level1 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level2 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          level3 {
            id
            text {
              en_GB
              zh_HK
              zh_CN
            }
          }
          floor
          issueVerified
          note
          unit
        }
        images {
          issueDetected
          issueVerified
          note
          url
          path
        }
      }
    }
  }
`;

export const QUERY_SETTINGS = gql`
  query settingsQuery($userId: ID, $organisationId: ID) {
    settings(userId: $userId, organisationId: $organisationId) {
      watermark {
        showAgency
        showBranch
        showAgent
      }
    }
  }
`;

export const UPDATE_SETTINGS = gql`
  mutation updateSettingsMutation($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      settings {
        watermark {
          showAgency
          showBranch
          showAgent
        }
      }
    }
  }
`;

export const LibraryQuery = gql`
  query LibraryQueryData(
    $level5Id: String
    $operations: String!
    $isAuction: Boolean
  ) {
    galleries(level5Id: $level5Id, isAuction: $isAuction) {
      data {
        templateUrl
        caption {
          en_GB
          zh_HK
          zh_CN
          ms_MY
        }
        imageSourceCode
      }
      variables {
        size
      }
      sharpieUrls {
        path
        url
        sharpieUrl(operations: $operations)
        width
        height
        caption {
          en_GB
          zh_HK
          zh_CN
          ms_MY
        }
        imageSourceCode
      }
    }
  }
`;

export const LEADS_LIST_QUERY = gql`
  query leadsQuery($statusCode: Int, $page: Int, $limit: Int) {
    leadList(statusCode: $statusCode, page: $page, limit: $limit) {
      pagination {
        total
        nextPageToken
        limit
        page
      }
      totalPerStatus {
        statusCode
        total
      }
      data {
        id
        listingId
        listerId
        date
        enquirerName
        enquirerEmail
        enquirerPhone
        message
        country
        isRead
        statusCode
      }
    }
  }
`;

export const ARCHIVE_LEADS = gql`
  mutation archiveLead($input: ArchiveLeadInput!) {
    archiveLead(input: $input) {
      id
    }
  }
`;

export const READ_LEADS = gql`
  mutation readLead($input: ReadLeadInput!) {
    readLead(input: $input) {
      id
    }
  }
`;

export const UNREAD_LEADS = gql`
  mutation unreadLead($input: UnreadLeadInput!) {
    unreadLead(input: $input) {
      id
    }
  }
`;

export const RESTORE_ARCHIVE_LEAD = gql`
  mutation restoreArchiveLead($input: RestoreArchiveLeadInput!) {
    restoreArchiveLead(input: $input) {
      id
    }
  }
`;

export const EMPTY_ARCHIVE_LEAD = gql`
  mutation emptyArchiveLead {
    emptyArchiveLead {
      leadHasDeleted
    }
  }
`;

export const DELETE_LEAD = gql`
  mutation deleteLead($input: DeleteLeadInput!) {
    deleteLead(input: $input) {
      leadHasDeleted
    }
  }
`;

export const LOCATION_LEVEL1_QUERY = gql`
  query level1Query {
    places(q: "level1", limit: 50) {
      data {
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const LOCATION_LEVEL1_QUERY_V2 = gql`
  query level1QueryV2 {
    placesV2(q: "level1", limit: 50) {
      data {
        level1 {
          nanoId
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const LOCATION_LEVEL2_QUERY = gql`
  query level2Query($keyword: String, $level1Id: String) {
    places(keyword: $keyword, q: "level2", level1Id: $level1Id, limit: 1000) {
      data {
        latitude
        longitude
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const LOCATION_LEVEL2_V2_QUERY = gql`
  query level2QueryV2($keyword: String, $level1Id: String) {
    placesV2(
      keyword: $keyword
      q: "level2"
      parentLevel: "level1"
      parentNanoId: $level1Id
      limit: 1000
    ) {
      data {
        latitude
        longitude
        level1 {
          nanoId
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          nanoId
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
      }
    }
  }
`;

export const BUILDING_SEARCH = gql`
  query buildingQuery(
    $keyword: String
    $q: String = "level3"
    $shouldExtendsFields: Boolean = false
    $includeBuildingFacilityCodes: Boolean = false
  ) {
    places(
      searchAddress: true
      keyword: $keyword
      q: $q
      limit: 50
      includeBuildingFacilityCodes: $includeBuildingFacilityCodes
    ) {
      data {
        latitude
        longitude
        address {
          en_GB
          zh_HK
          zh_CN
        }
        postCode
        level1 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level2 {
          id
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level3 {
          id @skip(if: $shouldExtendsFields)
          text {
            en_GB
            zh_HK
            zh_CN
          }
        }
        level5 @include(if: $shouldExtendsFields) {
          id
          text {
            en_GB
          }
        }
        propertyType {
          id
          code
          description
          label
        }
        propertyGroupType {
          id
          code
          description
          label
        }
        buildingFacilities @include(if: $includeBuildingFacilityCodes) {
          id
          code
          description
        }
      }
    }
  }
`;

export const QUERY_AGENTOOLS = gql`
  query agentoolsListQuery(
    $listingTypeCode: Int
    $propertyTypeCode: [Int]
    $channelCode: Int
    $fromDate: String
    $toDate: String
    $fromPrice: Float
    $toPrice: Float
    $sortBy: String
    $limit: Int
    $page: String
    $keyword: String
  ) {
    agentools(
      listingTypeCode: $listingTypeCode
      propertyTypeCode: $propertyTypeCode
      channelCode: $channelCode
      fromDate: $fromDate
      toDate: $toDate
      fromPrice: $fromPrice
      toPrice: $toPrice
      sortBy: $sortBy
      limit: $limit
      page: $page
      keyword: $keyword
    ) {
      data {
        linkToPDP
        id
        price
        listingId
        channel {
          id
          code
          description
          label
        }
        propertyType {
          id
          code
          description
          label
        }
        description
        source {
          id
          code
          description
          label
        }
        date
        agencyLicenseNo
        contactNo
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const QUERY_REFERENCE_TYPES = gql`
  query referenceTypes($names: String!) {
    referenceTypes(names: [$names]) {
      name
      values {
        id
        code
        description
      }
    }
  }
`;

export const QUERY_PROPERTY_WANTED = gql`
  query propertyWantedList(
    $keyword: String
    $channelCode: Int
    $propertyWantedGroupTypeCode: Int
    $budgetFrom: Int
    $budgetTo: Int
    $bedroom: Int
    $bathroom: Int
    $postedDateFrom: String
    $postedDateTo: String
    $sortBy: String
    $limit: Int
    $page: Int
  ) {
    propertyWantedList(
      keyword: $keyword
      channelCode: $channelCode
      propertyWantedGroupTypeCode: $propertyWantedGroupTypeCode
      budgetFrom: $budgetFrom
      budgetTo: $budgetTo
      bedroom: $bedroom
      bathroom: $bathroom
      postedDateFrom: $postedDateFrom
      postedDateTo: $postedDateTo
      sortBy: $sortBy
      limit: $limit
      page: $page
    ) {
      data {
        id
        market
        channelCodes
        channelCodeText
        propertyWantedGroupTypeText
        propertyWantedGroupTypeCode {
          description
        }
        budgetMin
        budgetMax
        roomText
        combinedLocationText
        postedDate
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const QUERY_PROPERTY_WANTED_BY_ID = gql`
  query propertyWanted($id: ID!) {
    propertyWanted(id: $id) {
      id
      consumer {
        name
        contactNo
        email
      }
      remarks
      market
      channelCodes
      channelCodeText
      propertyType {
        description
      }
      propertyWantedGroupTypeText
      propertyWantedGroupTypeCode {
        description
      }
      budgetMin
      budgetMax
      bedroom
      bathroom
      location1 {
        level1 {
          text {
            en_GB
          }
        }
        level2 {
          text {
            en_GB
          }
        }
      }
      location2 {
        level1 {
          text {
            en_GB
          }
        }
        level2 {
          text {
            en_GB
          }
        }
      }
      location3 {
        level1 {
          text {
            en_GB
          }
        }
        level2 {
          text {
            en_GB
          }
        }
      }
      postedDate
    }
  }
`;

export const FSBO_QUERY = gql`
  query fsboQuery {
    fsbos {
      data {
        id
        channel {
          description
          code
        }
        propertyType {
          description
          code
        }
        locationKeyword
        minPrice
        maxPrice
        date
      }
    }
  }
`;

export const DELETE_FSBO = gql`
  mutation deleteFsboMutation($input: DeleteFsboInput!) {
    deleteFsbo(input: $input) {
      fsboHasDeleted
    }
  }
`;

export const ADD_FSBO = gql`
  mutation addFsboMutation($input: AddFsboInput!) {
    addFsbo(input: $input) {
      fsbo {
        channel {
          code
          description
        }
        propertyType {
          code
          description
        }
        locationKeyword
        minPrice
        maxPrice
      }
    }
  }
`;

export const ADD_BUILDING = gql`
  mutation addBuildingMutation($input: NewBuildingInput!) {
    requestNewBuilding(input: $input) {
      data
    }
  }
`;

export const MA_LOCATION_SEARCH = gql`
  query maLocationQuery($keyword: String) {
    cities: places(q: "level2", keyword: $keyword, includePlaceId: true) {
      data {
        level1 {
          id
          text {
            en_GB
            ms_MY
          }
        }
        level2 {
          id
          text {
            en_GB
            ms_MY
          }
          placeId
        }
      }
    }
    buildings: places(q: "level5", keyword: $keyword, includePlaceId: true) {
      data {
        propertyType {
          description
        }
        level1 {
          id
          text {
            en_GB
            ms_MY
          }
        }
        level2 {
          id
          text {
            en_GB
            ms_MY
          }
          placeId
        }
        level3 {
          id
          text {
            en_GB
            ms_MY
          }
        }
        level5 {
          id
          text {
            en_GB
            ms_MY
          }
          placeId
        }
      }
    }
  }
`;

export const MA_LOCATION_SEARCH_LSOT = gql`
  query maLocationLsotQuery($keyword: String!) {
    cities: autocomplete(
      levels: "level2"
      keyword: $keyword
      resolveLocation: true
    ) {
      data {
        level1 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level2 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
      }
    }
    townships: autocomplete(
      levels: "level3"
      keyword: $keyword
      resolveLocation: true
    ) {
      data {
        level1 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level2 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level3 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
      }
    }
    buildings: autocomplete(
      levels: "level5"
      keyword: $keyword
      resolveLocation: true
    ) {
      data {
        propertyType {
          code
          description
        }
        level1 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level2 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level3 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
        level5 {
          nanoId
          text {
            en_GB
            ms_MY
          }
        }
      }
    }
  }
`;

export const MA_REFERENCES_QUERY = gql`
  query maReferencesQuery {
    references {
      channels {
        id
        code
        description
        label
      }
      propertyCategoryTypes {
        id
        code
        description
        label
        children {
          id
          code
          description
          label
          children {
            id
            code
            description
            label
          }
        }
      }
      combinedPropertyTypes {
        parent {
          code
        }
        code
        description
        id
        label
      }
    }
  }
`;

export const ANNOUNCEMENT_QUERY = gql`
  query announcement($id: ID!) {
    announcement(id: $id) {
      id
      market
      content
      postedDate
      title
      statusCode
      typeCode
      startDate
      endDate
      description {
        en_GB
      }
      titleV2 {
        en_GB
      }
    }
  }
`;

export const GET_ANNOUNCEMENT_LIST = gql`
  query announcementListQuery(
    $typeCode: Int
    $statusCode: Int
    $keyword: String
    $dateFrom: String
    $dateTo: String
    $sortBy: String
    $limit: Int
    $pageToken: String
    $page: Int
  ) {
    announcementList(
      typeCode: $typeCode
      statusCode: $statusCode
      keyword: $keyword
      dateFrom: $dateFrom
      dateTo: $dateTo
      sortBy: $sortBy
      limit: $limit
      pageToken: $pageToken
      page: $page
    ) {
      pagination {
        total
        limit
        page
      }
      data {
        id
        market
        title
        postedDate
        statusCode
        typeCode
        titleV2 {
          en_GB
        }
        endDate
        startDate
        description {
          en_GB
        }
      }
    }
  }
`;

export const CREATE_ANNOUNCEMENT = gql`
  mutation CreateAnnouncement($input: CreateAnnouncementInput!) {
    createAnnouncement(input: $input) {
      announcement {
        id
        market
        title
        content
        postedDate
        description {
          en_GB
        }
        startDate
        endDate
        titleV2 {
          en_GB
        }
        contentV2 {
          en_GB
        }
      }
    }
  }
`;

export const UPDATE_ANNOUNCEMENT = gql`
  mutation UpdateAnnouncement($input: UpdateAnnouncementInput!) {
    updateAnnouncement(input: $input) {
      announcement {
        id
        market
        title
        content
        postedDate
        description {
          en_GB
        }
        startDate
        endDate
        titleV2 {
          en_GB
        }
        contentV2 {
          en_GB
        }
      }
    }
  }
`;

export const DELETE_ANNOUNCEMENT = gql`
  mutation deleteAnnouncementMutation($input: DeleteAnnouncementInput!) {
    deleteAnnouncement(input: $input) {
      announcementHasDeleted
    }
  }
`;

export const MA_MARKET_COMPARISON_QUERY = gql`
  query MAMarketComparisonQuery(
    $locationLevel1Id: ID!
    $locationLevel2Id: ID!
    $locationLevel3Id: ID
    $channelCode: Int!
    $propertyCategoryTypeCode: Int!
    $propertyTypeCode: Int
  ) {
    maMarketComparison(
      locationLevel1Id: $locationLevel1Id
      locationLevel2Id: $locationLevel2Id
      locationLevel3Id: $locationLevel3Id
      channelCode: $channelCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      propertyTypeCode: $propertyTypeCode
    ) {
      startDate
      endDate
      totalRank
      transactionVolumeRank
      transactionPriceRank
      impressionRank
      data {
        location {
          level2 {
            nanoId
            text {
              en_GB
            }
          }
          level3 {
            nanoId
            text {
              en_GB
            }
          }
        }
        totalAvgDailyImpressionPerListing
        avgImpressionGrowth
        medianTransaction
        medianPrice
      }
    }
  }
`;

export const MA_BUILDING_COMPARISON_QUERY = gql`
  query MABuildingComparisonQuery(
    $locationLevel2Id: ID!
    $locationLevel3Id: ID
    $channelCode: Int!
    $propertyCategoryTypeCode: Int!
    $propertyTypeCode: Int
    $locationLevel5Id: ID
  ) {
    maBuildingComparison(
      locationLevel2Id: $locationLevel2Id
      locationLevel3Id: $locationLevel3Id
      channelCode: $channelCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      propertyTypeCode: $propertyTypeCode
      locationLevel5Id: $locationLevel5Id
    ) {
      startDate
      endDate
      highestMedianSold {
        level5 {
          nanoId
          text {
            en_GB
          }
        }
      }
      highestMedianPrice {
        level5 {
          nanoId
          text {
            en_GB
          }
        }
      }
      highestImpressionsPerListing {
        level5 {
          nanoId
          text {
            en_GB
          }
        }
      }
      overview {
        totalRank
        totalAvgDailyImpressionPerListing
        impressionRank
        medianTransaction
        transactionVolumeRank
        medianPrice
        transactionPriceRank
      }
      data {
        location {
          level2 {
            nanoId
            text {
              en_GB
            }
          }
          level3 {
            nanoId
            text {
              en_GB
            }
          }
          level5 {
            nanoId
            text {
              en_GB
            }
          }
        }
        propertyType {
          id
          code
          description
        }
        totalAvgDailyImpressionPerListing
        avgImpressionGrowth
        medianTransaction
        medianPrice
      }
    }
  }
`;

export const MA_VISIT_TIMES_QUERY = gql`
  query MAVisitTimesQuery(
    $locationLevel2Id: ID!
    $locationLevel3Id: ID
    $channelCode: Int!
    $propertyTypeCode: Int
    $propertyCategoryTypeCode: Int!
  ) {
    maVisitTime(
      locationLevel2Id: $locationLevel2Id
      locationLevel3Id: $locationLevel3Id
      channelCode: $channelCode
      propertyTypeCode: $propertyTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
    ) {
      startDate
      endDate
      busiestTimeStart
      lowestTimeStart
      timeFrameLength
      busiestDay {
        description
      }
      lowestDay {
        description
      }
      data {
        day
        hour
        value
      }
    }
  }
`;

export const MA_SUMMARY_QUERY = gql`
  query MASummaryQuery(
    $locationLevel1Id: ID
    $locationLevel2Id: ID!
    $locationLevel5Id: ID
    $channelCode: Int!
    $propertyTypeCode: Int
    $propertyCategoryTypeCode: Int!
  ) {
    maSummary(
      locationLevel1Id: $locationLevel1Id
      locationLevel2Id: $locationLevel2Id
      locationLevel5Id: $locationLevel5Id
      channelCode: $channelCode
      propertyTypeCode: $propertyTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
    ) {
      startDate
      endDate
      featuredMultiplier
      premiumMultiplier
    }
  }
`;

export const MA_OVERVIEW_QUERY = gql`
  query MAOverviewQuery(
    $locationLevel2Id: ID!
    $locationLevel3Id: ID
    $locationLevel5Id: ID
    $channelCode: Int!
    $propertyTypeCode: Int
    $propertyCategoryTypeCode: Int!
  ) {
    maOverview(
      locationLevel2Id: $locationLevel2Id
      locationLevel3Id: $locationLevel3Id
      locationLevel5Id: $locationLevel5Id
      channelCode: $channelCode
      propertyTypeCode: $propertyTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
    ) {
      startDate
      endDate
      totalRank
      totalAvgDailyImpressionPerListing
      impressionRank
      avgImpressionGrowth
      impressionGrowth
      listingGrowth
      listingImpressions {
        date
        totalAvgDailyImpressionPerListing
        avgImpressionGrowth
        isPeakMonth
      }
      tierWithMostAvgImpressions {
        id
        code
        description
      }
      tierWithMostAvgImpressionsMultiplier
      standardAvgImpressions
      featuredAvgImpressions
      featuredMultiplier
      premiumAvgImpressions
      premiumMultiplier
      transactionStartDate
      transactionEndDate
      medianTransaction
      transactionVolumeRank
      highestTransactionVolume
      highestTransactionDate
      lowestTransactionVolume
      lowestTransactionDate
      transactions {
        date
        transactionVolume
        isPeak
      }
      medianPrice
      transactionPriceRank
      mostSoldSizeRange
      mostSoldSizeRangeMedianPrice
      leastSoldSizeRange
      leastSoldSizeRangeMedianPrice
      transactionsSizeRange {
        sizeRange
        medianPrice
        medianTransaction
        totalTransactions
        sizeRangeMarketShare
        isMostSold
        isLeastSold
        propertyTypeRank
        sizeRangePropertyTypeBreakDowns {
          propertyType {
            description
          }
          totalTransactions
          propertyTypeRank
        }
      }
      rentSizeRange {
        sizeRange
        sizeRangeRoomBreakDowns {
          bedroomNo
          medianPrice
        }
      }
      popularBuildingType {
        propertyType {
          description
        }
        medianTransaction
        medianPrice
        totalAvgDailyImpressionPerListing
      }
    }
  }
`;
export const MA_CONSUMER_SRP_QUERY = gql`
  query maConsumerSRP(
    $nanoId: String
    $locationLevel1Text: String
    $locationLevel2Text: String
    $channelCode: Int!
    $propertyTypeCode: Int
    $propertyCategoryTypeCode: Int!
  ) {
    maSRP(
      nanoId: $nanoId
      locationLevel1Text: $locationLevel1Text
      locationLevel2Text: $locationLevel2Text
      channelCode: $channelCode
      propertyTypeCode: $propertyTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
    ) {
      summary {
        tier
        total
      }
      pageRange {
        tier
        pageStart
        pageStartBuffered
        pageEnd
      }
    }
  }
`;

export const SCHEDULE_REFERENCES_QUERY = gql`
  query ScheduleReferencesQuery {
    references {
      schedulerTypes {
        id
        code
        description
      }
      schedulerStatus {
        id
        code
        description
      }
      days {
        id
        code
        description
      }
    }
  }
`;

export const CREATE_READVERTISE_SCHEDULER = gql`
  mutation CreateReAdvertiseScheduler(
    $input: CreateReAdvertiseSchedulerInput!
  ) {
    createReAdvertiseScheduler(input: $input) {
      reAdvertiseScheduler {
        id
        name {
          en_GB
          ms_MY
        }
        type {
          id
          code
          label
          description
        }
      }
    }
  }
`;

export const VALIDATE_SCHEDULER_CREDITS = gql`
  mutation ValidateSchedulerCredits($input: CreateReAdvertiseSchedulerInput!) {
    preValidateReAdvertiseScheduler(input: $input) {
      reAdvertiseSchedulerInventory {
        creditUsage {
          totalOrgCost
          totalOwnCost
        }
      }
    }
  }
`;

export const READVERTISE_SCHEDULER_LIST_QUERY = gql`
  query reAdvertiseSchedulerList(
    $listingId: ID
    $statusCode: Int
    $limit: Int
    $page: Int
  ) {
    reAdvertiseSchedulerList(
      listingId: $listingId
      statusCode: $statusCode
      limit: $limit
      page: $page
    ) {
      pagination {
        nextPageToken
        limit
        page
        total
      }
      data {
        id
        name {
          en_GB
          zh_HK
          zh_CN
        }
        type {
          id
          code
          description
        }
        startDate
        endDate
        createdDate
        times
        days {
          id
          code
          description
        }
        numberOfListing
      }
    }
  }
`;

export const READVERTISE_SCHEDULER_LISTINGS_QUERY = gql`
  query reAdvertiseSchedulerListings(
    $id: ID!
    $shouldExtendsFields: Boolean = true
  ) {
    reAdvertiseSchedulerListings(id: $id) {
      data {
        ...BaseListingInfoFragment
      }
    }
  }
  ${BASE_LISTING_INFO_FRAGMENT}
`;

export const READVERTISE_SCHEDULER_LISTING_JOB_LIST_QUERY = gql`
  query reAdvertiseSchedulerListingJobList($id: ID!, $listingId: ID!) {
    reAdvertiseSchedulerListingJobList(id: $id, listingId: $listingId) {
      pagination {
        nextPageToken
        limit
        page
        total
      }
      data {
        id
        listingId
        schedulerId
        schedulerDateTime
        executedDateTime
        propertyCategoryType {
          id
          code
          description
        }
        product {
          id
          code
          description
        }
        status {
          id
          code
          description
        }
        channel {
          id
          code
          description
        }
        cost
      }
    }
  }
`;

export const READVERTISE_LIST_QUERY = gql`
  query ReAdvertiseListQuery(
    $id: ID
    $statusCode: Int
    $limit: Int
    $page: Int
  ) {
    reAdvertiseSchedulerList(
      listingId: $id
      statusCode: $statusCode
      limit: $limit
      page: $page
    ) {
      data {
        id
        name {
          en_GB
          ms_MY
        }
        type {
          id
          code
          description
        }
        startDate
        endDate
        times
        days {
          id
          code
          description
        }
        numberOfListing
      }
    }
  }
`;

export const READVERTISE_LISTINGS_QUERY = gql`
  query ReAdvertiseListings($id: ID!, $shouldExtendsFields: Boolean = false) {
    quotes(productTypeCode: 2) {
      quoteId
      title
      remaining
      productCode
    }
    reAdvertiseSchedulerListings(id: $id) {
      data {
        id
        salePrice {
          fixed
        }
        rentPrice {
          fixed
        }
        listingProduct {
          ...ReferenceFields
        }
        propertyType {
          ...ReferenceFields
        }
        location {
          level1 {
            id
            text {
              ...MultiLangTextFields
            }
          }
          level2 {
            id
            text {
              ...MultiLangTextFields
            }
          }
          level3 {
            id
            text {
              ...MultiLangTextFields
            }
          }
          level4 @include(if: $shouldExtendsFields) {
            id
            text {
              ...MultiLangTextFields
            }
          }
          level5 @include(if: $shouldExtendsFields) {
            id
            text {
              ...MultiLangTextFields
            }
          }
          address {
            ...MultiLangTextFields
          }
          floor
          unit
          latitude
          longitude
          hideUnitFloor
          carParkNo
          hideCarParkNo
          showPIN
          hideLocationLevel3
          postalCode
          block
          hiddenAddress
        }
      }
    }
  }
  ${FRAGMENT_MULTILINE_FIELD}
  ${FRAGMENT_REFERENCE_FIELD}
`;

export const CANCEL_READVERTISE_SCHEDULER = gql`
  mutation CancelReAdvertiseScheduler(
    $input: CancelReAdvertiseSchedulerInput!
  ) {
    cancelReAdvertiseScheduler(input: $input) {
      reAdvertiseScheduler {
        listingIds
      }
    }
  }
`;

export const QUERY_SCHEDULER_LIST_OF_LISTING = gql`
  query QuerySchedulerListOfListing($id: ID!) {
    reAdvertiseSchedulerList(listingId: $id) {
      data {
        id
      }
    }
  }
`;

export const MA_PLACE_ID_QUERY = gql`
  query FindPlaceId($level2Id: String, $level5Id: String, $q: String) {
    places(
      level2Id: $level2Id
      level5Id: $level5Id
      q: $q
      includePlaceId: true
    ) {
      data {
        level2 {
          placeId
        }
        level5 {
          placeId
        }
      }
    }
  }
`;

export const BRICKZ_QUERY = gql`
  query brickz {
    brickz {
      token
    }
  }
`;

export const HOME_REPORT_QUERY = gql`
  query iqAgent {
    iqAgent {
      token
    }
  }
`;

export const ENQUIRY_STATISTICS_QUERY = gql`
  query enquiryStatistics(
    $enquiryType: String
    $period: Int
    $agencyId: Int
    $branchId: Int
    $agentId: Int
    $developerId: Int
    $servicedApartmentId: String
  ) {
    enquiryStatistics(
      enquiryType: $enquiryType
      period: $period
      agencyId: $agencyId
      branchId: $branchId
      agentId: $agentId
      developerId: $developerId
      servicedApartmentId: $servicedApartmentId
    ) {
      processedEnquiries
      sentSuccessEnquiries
      bouncedEnquiries
      quarantinedEnquiries
      statistics {
        enquiryDate
        delivery
        bounced
        quarantined
      }
      quarantinedBreakdown {
        emails
        ipAddress
        urls
        wordsAndPhrases
        rateLimit
      }
    }
  }
`;

export const ENQUIRY_LIST_QUERY = gql`
  query enquiryList(
    $period: Int
    $enquiryType: String
    $status: String
    $quarantinedReasons: [String]
    $keyword: String
    $page: Int
    $limit: Int
  ) {
    enquiryList(
      period: $period
      enquiryType: $enquiryType
      status: $status
      quarantinedReasons: $quarantinedReasons
      keyword: $keyword
      page: $page
      limit: $limit
    ) {
      data {
        enquiryId
        date
        listingId
        enquiryType
        fromEmail
        toEmail
        name
        status
        emailList {
          emailId
          toEmail
          status
        }
        lastAction
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const ENQUIRY_DEVELOPERS = gql`
  query developers($keyword: String, $type: String) {
    developers(keyword: $keyword, type: $type) {
      id
      name
    }
  }
`;

export const ENQUIRY_SERVICED_APARTMENT = gql`
  query servicedApartments($keyword: String) {
    servicedApartments(keyword: $keyword) {
      id
      title
    }
  }
`;

export const ENQUIRY_DETAIL_QUERY = gql`
  query enquiryDetail($enquiryId: Int) {
    enquiryDetail(enquiryId: $enquiryId) {
      enquiryId
      date
      listingId
      spamReason
      enquiryType
      fromEmail
      name
      status
      message
      badWord
      emailList {
        emailId
        toEmail
        status
      }
      actionHistory {
        action
        date
      }
    }
  }
`;

export const BLOCK_ITEMS_QUERY = gql`
  query blockItems(
    $blockType: String
    $keyword: String
    $sortBy: String
    $page: Int
    $limit: Int
  ) {
    blockItems(
      blockType: $blockType
      keyword: $keyword
      sortBy: $sortBy
      page: $page
      limit: $limit
    ) {
      data {
        id
        market
        type
        content
        createdDate
        updatedDate
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const DELETE_BLOCK_ITEM = gql`
  mutation deleteBlockItem($id: Int) {
    deleteBlockItem(blockItemId: $id)
  }
`;

export const CREATE_BLOCK_ITEM = gql`
  mutation createBlockItem($input: BlockItemInput) {
    createBlockItem(input: $input) {
      id
    }
  }
`;

export const BLOCK_ENQUIRY = gql`
  mutation blockEnquiry($enquiryId: Int) {
    blockEnquiry(enquiryId: $enquiryId) {
      action
      email
      date
    }
  }
`;

export const RELEASE_ENQUIRY = gql`
  mutation releaseEnquiry($enquiryId: Int) {
    releaseEnquiry(enquiryId: $enquiryId) {
      action
      email
      date
    }
  }
`;

export const RESEND_ENQUIRY = gql`
  mutation resendEnquiry($enquiryId: Int, $input: emailsInput!) {
    resendEnquiry(enquiryId: $enquiryId, input: $input) {
      action
      emails
      date
    }
  }
`;

export const FORWARD_ENQUIRY = gql`
  mutation forwardEnquiry($enquiryId: Int, $input: emailsInput!) {
    forwardEnquiry(enquiryId: $enquiryId, input: $input) {
      action
      emails
      date
    }
  }
`;

export const ENQUIRY_CONFIG_LIST_QUERY = gql`
  query enquiryConfigList {
    enquiryConfigList {
      id
      key
      value
    }
  }
`;

export const UPDATE_ENQUIRY_CONFIG = gql`
  mutation updateEnquiryConfig($input: EnquiryConfigInput!) {
    updateEnquiryConfig(input: $input) {
      id
      value
    }
  }
`;

export const GET_INBOX_STATISTICS_QUERY = gql`
  query inboxStatistic($status: String) {
    inboxStatistic(status: $status) {
      data {
        unread
        archived
        nonresponded
      }
    }
  }
`;

export const SPECIALIST_RECOMMENDATION = gql`
  query specialistRecommendation($listingCreationV3: Boolean = false) {
    specialistRecommendation {
      data {
        propertyGroupType @skip(if: $listingCreationV3) {
          description
          code
        }
        propertyCategoryType @include(if: $listingCreationV3) {
          description
          code
        }
        type {
          description
          code
        }
        building {
          id
          text {
            en_GB
          }
        }
        locationLevel1 {
          id
          text {
            en_GB
          }
        }
        locationLevel2 {
          id
          text {
            en_GB
          }
        }
        activeListings
        slotsAvailable
      }
    }
  }
`;

export const SPECIALIST_SUMMARY = gql`
  query summarySpecialist {
    summarySpecialist {
      specialistExists
      errors
      config {
        listingNumber
        productGroup {
          description
        }
      }
    }
  }
`;

export const SPECIALIST_SLOT_QUERY = gql`
  query specialistSlotList($listingCreationV3: Boolean = false) {
    specialistSlotList {
      data {
        id
        type {
          description
          code
        }
        slotNumber
        listingNumber
        productGroup {
          description
          code
        }
        propertyGroupType @skip(if: $listingCreationV3) {
          description
          code
        }
        propertyCategoryType @include(if: $listingCreationV3) {
          description
          code
        }
        provisionProductCode {
          description
          code
        }
        provisionTotal
        updatedDate
      }
    }
  }
`;

export const SPECIALIST_SLOT_BY_ID_QUERY = gql`
  query specialistSlot($id: ID!, $listingCreationV3: Boolean = false) {
    specialistSlot(id: $id) {
      id
      type {
        description
        code
      }
      slotNumber
      listingNumber
      productGroup {
        description
        code
      }
      propertyGroupType @skip(if: $listingCreationV3) {
        description
        code
      }
      propertyCategoryType @include(if: $listingCreationV3) {
        description
        code
      }
      provisionProductCode {
        description
        code
      }
      provisionTotal
      updatedDate
    }
  }
`;

export const SPECIALIST_SLOT_UPDATE_QUERY = gql`
  mutation updateSpecialistSlotMutation(
    $input: UpdateSpecialistSlotInput!
    $listingCreationV3: Boolean = false
  ) {
    updateSpecialistSlot(input: $input) {
      specialistSlot {
        id
        type {
          description
          code
        }
        slotNumber
        listingNumber
        productGroup {
          description
          code
        }
        propertyCategoryType @include(if: $listingCreationV3) {
          description
          code
        }
        propertyGroupType @skip(if: $listingCreationV3) {
          description
          code
        }
        provisionProductCode {
          description
          code
        }
        provisionTotal
        updatedDate
      }
    }
  }
`;

export const SPECIALIST_SLOT_REFERENCE_FRAGMENT_QUERY = gql`
  fragment specialistSlotReferencesFragment on References {
    specialistType {
      code
      description
    }
    listingProductGroup {
      code
      description
    }
  }
`;

export const SPECIALIST_SLOT_REFERENCE_QUERY = gql`
  query specialistSlotReferencesQuery {
    references {
      ...specialistSlotReferencesFragment
    }
  }
  ${SPECIALIST_SLOT_REFERENCE_FRAGMENT_QUERY}
`;

export const SPECIALIST_QUERY = gql`
  query specialistList(
    $typeCode: Int
    $buildingId: String
    $locationLevel1Id: String
    $locationLevel2Id: String
    $userId: Int
    $statusCode: Int
    $statusCodes: [Int]
    $propertyGroupTypeCode: Int
    $propertyCategoryTypeCode: Int
    $limit: Int
    $page: String
    $sortBy: String
    $listingCreationV3: Boolean = false
  ) {
    specialistList(
      typeCode: $typeCode
      buildingId: $buildingId
      locationLevel1Id: $locationLevel1Id
      locationLevel2Id: $locationLevel2Id
      userId: $userId
      statusCode: $statusCode
      statusCodes: $statusCodes
      propertyGroupTypeCode: $propertyGroupTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      limit: $limit
      page: $page
      sortBy: $sortBy
    ) {
      pagination {
        nextPageToken
        limit
        page
        total
      }
      data {
        id
        extendSpecialistId
        user {
          id
          fullName {
            en_GB
          }
        }
        startDate
        expiryDate
        status {
          code
          description
        }
        locationLevel1 {
          id
          text {
            en_GB
          }
        }
        locationLevel2 {
          id
          text {
            en_GB
          }
        }
        building {
          id
          text {
            en_GB
          }
        }
        propertyGroupType @skip(if: $listingCreationV3) {
          code
          description
        }
        propertyCategoryType @include(if: $listingCreationV3) {
          code
          description
        }
        durationMonth {
          code
          description
        }
      }
    }
  }
`;

export const SPECIALIST_REFERENCE_FRAGMENT_QUERY = gql`
  fragment specialistReferencesFragment on References {
    propertyGroupTypes @skip(if: $listingCreationV3) {
      id
      code
      description
      label
    }
    propertyCategoryTypes @include(if: $listingCreationV3) {
      id
      code
      description
      label
    }
    specialistType {
      id
      code
      description
      label
    }
    specialistStatus {
      id
      code
      description
      label
    }
    specialistDuration {
      id
      code
      description
      label
    }
  }
`;

export const SPECIALIST_REFERENCE_QUERY = gql`
  query specialistReferencesQuery($listingCreationV3: Boolean = false) {
    references {
      ...specialistReferencesFragment
    }
  }
  ${SPECIALIST_REFERENCE_FRAGMENT_QUERY}
`;

export const SPECIALIST_REPORT_REFERENCE_FRAGMENT_QUERY = gql`
  fragment specialistReportReferencesFragment on References {
    propertyGroupTypes @skip(if: $listingCreationV3) {
      id
      code
      description
      label
    }
    propertyCategoryTypes @include(if: $listingCreationV3) {
      id
      code
      description
      label
    }
    specialistStatus {
      id
      code
      description
      label
    }
  }
`;

export const SPECIALIST_REPORT_REFERENCE_QUERY = gql`
  query specialistReportReferencesQuery($listingCreationV3: Boolean = false) {
    references {
      ...specialistReportReferencesFragment
    }
  }
  ${SPECIALIST_REPORT_REFERENCE_FRAGMENT_QUERY}
`;

export const SPECIALIST_CREATE_QUERY = gql`
  mutation addSpecialistMutation(
    $input: AddSpecialistInput!
    $listingCreationV3: Boolean = false
  ) {
    addSpecialist(input: $input) {
      specialist {
        id
        locationLevel1 {
          id
          text {
            en_GB
          }
        }
        locationLevel2 {
          id
          text {
            en_GB
          }
        }
        building {
          id
          text {
            en_GB
          }
        }
        propertyGroupType @skip(if: $listingCreationV3) {
          code
          description
        }
        propertyCategoryType @include(if: $listingCreationV3) {
          code
          description
        }
      }
    }
  }
`;

export const SPECIALIST_ACTIVATE_QUERY = gql`
  mutation activateSpecialistMutation($input: ActivateSpecialistInput!) {
    activateSpecialist(input: $input) {
      specialistHasActivated
    }
  }
`;

export const SPECIALIST_OFFLINE_QUERY = gql`
  mutation offlineSpecialistMutation($input: OfflineSpecialistInput!) {
    offlineSpecialist(input: $input) {
      specialistHasOffline
    }
  }
`;

export const SPECIALIST_DELETE_QUERY = gql`
  mutation deleteSpecialistMutation($input: DeleteSpecialistInput!) {
    deleteSpecialist(input: $input) {
      specialistHasDeleted
    }
  }
`;

export const SPECIALIST_EXTEND_QUERY = gql`
  mutation extendSpecialistMutation($input: ExtendSpecialistInput!) {
    extendSpecialist(input: $input) {
      specialist {
        id
      }
    }
  }
`;

export const SPECIALIST_CREDITS_QUERY = gql`
  query specialistCredits(
    $level1Id: String
    $level2Id: String
    $level5Id: String
    $productDuration: Int
    $propertyGroupTypeCode: Int
    $propertyCategoryTypeCode: Int
  ) {
    quotes(
      level1Id: $level1Id
      level2Id: $level2Id
      level5Id: $level5Id
      productDuration: $productDuration
      propertyGroupTypeCode: $propertyGroupTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      productTypeCode: 3
    ) {
      cost
      originalCost
      remaining
      currencyTypeCode
    }
  }
`;

export const SPECIALIST_PRE_VALIDATE_QUERY = gql`
  mutation preValidateSpecialistMutation($input: PreValidateSpecialistInput!) {
    preValidateSpecialist(input: $input) {
      specialistHasVerified
      errors
      config {
        slotNumber
        listingNumber
        productGroup {
          description
        }
      }
    }
  }
`;

export const SPECIALIST_STATS_QUERY = gql`
  query specialistStats(
    $typeCode: Int
    $buildingId: String
    $locationLevel1Id: String
    $locationLevel2Id: String
    $propertyGroupTypeCode: Int
    $propertyCategoryTypeCode: Int
  ) {
    specialistStats(
      typeCode: $typeCode
      buildingId: $buildingId
      locationLevel1Id: $locationLevel1Id
      locationLevel2Id: $locationLevel2Id
      propertyGroupTypeCode: $propertyGroupTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
    ) {
      listingNumber
      slotNumber
      nextAvailableSlot
      config {
        slotNumber
        provisionTotal
        provisionProduct {
          description
        }
      }
    }
  }
`;

export const SPECIALIST_NOTIFY_QUERY = gql`
  mutation addSpecialistNotificationMutation(
    $input: AddSpecialistNotificationInput!
  ) {
    addSpecialistNotification(input: $input) {
      id
    }
  }
`;

export const SPECIALIST_NOTIFICATION_BY_ID_QUERY = gql`
  query specialistNotification($id: ID!, $listingCreationV3: Boolean = false) {
    specialistNotification(id: $id) {
      id
      specialistType {
        description
        code
      }
      building {
        id
        text {
          en_GB
        }
      }
      locationLevel1 {
        id
        text {
          en_GB
        }
      }
      locationLevel2 {
        id
        text {
          en_GB
        }
      }
      propertyGroupType @skip(if: $listingCreationV3) {
        description
        code
      }
      propertyCategoryType @include(if: $listingCreationV3) {
        description
        code
      }
      durationMonth {
        description
        code
      }
    }
  }
`;

export const SPECIALIST_BY_ID_QUERY = gql`
  query specialist($id: ID!, $listingCreationV3: Boolean = false) {
    specialist(id: $id) {
      id
      type {
        description
        code
      }
      building {
        id
        text {
          en_GB
        }
      }
      locationLevel1 {
        id
        text {
          en_GB
        }
      }
      locationLevel2 {
        id
        text {
          en_GB
        }
      }
      propertyGroupType @skip(if: $listingCreationV3) {
        description
        code
      }
      propertyCategoryType @include(if: $listingCreationV3) {
        description
        code
      }
    }
  }
`;

export const SPECIALIST_PREMIUM_BALANCE_QUERY = gql`
  query specialistPremiumBalance($specialistIds: String!) {
    summaryUpgradeSpecialist(specialistIds: $specialistIds) {
      data {
        specialistId
        remaining
      }
    }
  }
`;

export const CREDIT_USAGE_REFERENCE_QUERY = gql`
  query creditUsageReferences {
    references {
      quoteTypes {
        id
        code
        description
      }
      listingActivityGroup {
        id
        code
        description
      }
    }
  }
`;

export const SPECIALIST_REPORT_QUERY = gql`
  query specialistReport(
    $typeCode: Int
    $locationLevel5Id: Int
    $locationLevel1Id: Int
    $locationLevel2Id: Int
    $locationLevel1NanoId: String
    $locationLevel2NanoId: String
    $locationLevel5NanoId: String
    $agentId: ID
    $statusCode: Int
    $propertyGroupTypeCode: Int
    $propertyCategoryTypeCode: Int
    $limit: Int
    $page: Int
    $sortBy: String
    $startDate: String
    $listingCreationV3: Boolean = false
  ) {
    specialistReport(
      typeCode: $typeCode
      locationLevel5Id: $locationLevel5Id
      locationLevel1Id: $locationLevel1Id
      locationLevel2Id: $locationLevel2Id
      locationLevel1NanoId: $locationLevel1NanoId
      locationLevel2NanoId: $locationLevel2NanoId
      locationLevel5NanoId: $locationLevel5NanoId
      startDate: $startDate
      agentId: $agentId
      statusCode: $statusCode
      propertyGroupTypeCode: $propertyGroupTypeCode
      propertyCategoryTypeCode: $propertyCategoryTypeCode
      limit: $limit
      page: $page
      sortBy: $sortBy
    ) {
      pagination {
        nextPageToken
        limit
        page
        total
      }
      data {
        agentID
        status
        agentName
        buildingName
        stateName
        cityName
        startDate
        durationMonths
        impressions
        clicks
        enquiries
        transactionID
        propertyGroupType @skip(if: $listingCreationV3)
        propertyCategoryType @include(if: $listingCreationV3)
      }
    }
  }
`;

export const CAPABILITY_QUERY = gql`
  query capability {
    capability {
      partnership
      partners {
        agentNet {
          sourceUid
        }
      }
    }
  }
`;

export const PRODUCT_LIST_QUERY = gql`
  query productList($categoryCodes: [Int]) {
    productList(categoryCodes: $categoryCodes) {
      data {
        id
        category {
          code
          description
        }
        type {
          code
          description
        }
        tier {
          code
          description
        }
        name
        description
        expiryDate
        price {
          currency
          grossAmount
          netAmount
          priceCode
        }
        validityUnit
        validityPeriod
        durationInDays
        isPromotion
        isBestValue
        relatedProducts {
          name
          totalUnit
          validityUnit
          validityPeriod
          category {
            code
            description
          }
          type {
            code
            description
          }
        }
      }
    }
  }
`;

const CART_FRAGMENT = gql`
  fragment CartFragment on Cart {
    id
    reaId
    stage
    couponCode
    renewal
    netAmount
    discountAmount
    grossAmount
    taxAmount
    discountTaxAmount
    currency
    taxRate
    errors
    netAmountBeforeDiscount
    items {
      product {
        id
        name
        category {
          code
          description
        }
        durationInDays
        validityPeriod
        validityUnit
      }
      quantity
      startDate
      packagePrice
      priceCode
    }
  }
`;

export const CART_CREATE_QUERY = gql`
  mutation createCartMutation($input: CartCreateInput!) {
    createCart(input: $input) {
      data {
        ...CartFragment
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_UPDATE_QUERY = gql`
  mutation updateCartMutation($input: CartUpdateInput!) {
    updateCart(input: $input) {
      data {
        ...CartFragment
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_CHECKOUT_QUERY = gql`
  mutation checkoutCartMutation($input: CartCheckoutInput!) {
    checkoutCart(input: $input) {
      data {
        ...CartFragment
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CREATE_PAYMENT_HISTORY_REPORT = gql`
  mutation createPaymentHistoryReportMutation(
    $input: CreatePaymentsHistoryReportInput!
  ) {
    createPaymentsHistoryReport(input: $input) {
      success
    }
  }
`;

export const CREATE_PAYMENT_QUOTE_QUERY = gql`
  mutation createPaymentQuoteMutation($input: CreatePaymentQuoteInput!) {
    createPaymentQuote(input: $input) {
      quote {
        hash
        invoiceId
        currency
        totalAmount
      }
      cart {
        ...CartFragment
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const GET_PAYMENT_QUOTE_QUERY = gql`
  query getPaymentQuote($quoteHash: String!) {
    getPaymentQuote(quoteHash: $quoteHash) {
      quote {
        hash
        invoiceId
        currency
        totalAmount
      }
      payment {
        timestamp
        description
        paymentId
        transactionId
        paymentType
        paymentStatus {
          code
          description
        }
        productCategoryCodes
      }
    }
  }
`;

export const PRICING_UPLOAD_LOG_QUERY = gql`
  query pricingUploadLogs($limit: Int, $page: String) {
    pricingUploadLogs(limit: $limit, page: $page) {
      data {
        id
        createdDate
        updatedDate
        s3File
        note
        uploadedBy
        statusCode
        projectPrices
        geolocationPrices
        scheduledDate
      }
      pagination {
        nextPageToken
        limit
        page
        total
      }
    }
  }
`;

export const PRICING_PUBLICATION_LOG_QUERY = gql`
  query pricingPublicationLogs($limit: Int, $page: String) {
    pricingPublicationLogs(limit: $limit, page: $page) {
      data {
        id
        createdDate
        updatedDate
        publishedBy
        action
        optimusUploadLogsId
      }
      pagination {
        nextPageToken
        limit
        page
        total
      }
    }
  }
`;

export const PRICING_DOWNLOAD_QUERY = gql`
  query pricingDownload($version: String) {
    pricingDownload(version: $version) {
      url
    }
  }
`;

export const PRICING_SCHEDULE_QUERY = gql`
  mutation schedulePricing($input: SchedulePricingInput!) {
    schedulePricing(input: $input) {
      success
    }
  }
`;

export const PRICING_UNSCHEDULE_QUERY = gql`
  mutation unschedulePricing($input: UnschedulePricingInput!) {
    unschedulePricing(input: $input) {
      success
    }
  }
`;

export const PRICING_PUBLISH_QUERY = gql`
  mutation publishPricing($input: PublishPricingInput!) {
    publishPricing(input: $input) {
      success
    }
  }
`;

export const PRICING_UPLOAD_QUERY = gql`
  mutation uploadPricing($input: UploadPricingInput!) {
    uploadPricing(input: $input) {
      success
    }
  }
`;

export const PRICING_SET_EMAIL_REMINDER_QUERY = gql`
  mutation setEmailReminder($input: EmailReminderInput!) {
    emailReminder(input: $input) {
      success
    }
  }
`;

export const PRICING_GET_EMAIL_REMINDER_QUERY = gql`
  query emailReminder {
    emailReminder {
      data {
        email
        fullName
      }
    }
  }
`;

export const INVOICE_QUERY = gql`
  query invoiceQuery($input: InvoiceInput) {
    getOrderInvoice(input: $input) {
      url
    }
  }
`;

export const PAYMENTS_HISTORY_QUERY = gql`
  query paymentsQuery($input: PaymentsInput) {
    getPayments(input: $input) {
      data {
        description
        paymentId
        transactionId
        paymentType
        paymentStatus {
          code
          description
        }
        agentName
        timestamp
        totalAmount
        invoiceId
        invoiceUrl
      }
      pagination {
        nextPageToken
        limit
        page
        total
      }
    }
  }
`;

export const SPECIALIST_TYPE_REFERENCE_QUERY = gql`
  query specialistTypeReferenceQuery {
    references {
      specialistType {
        id
        code
        description
        label
      }
    }
  }
`;

export const SPECIALIST_LIST_REPORT_QUERY = gql`
  query specialistListReport($specialistIds: [ID], $typeCode: Int) {
    specialistReport(specialistIds: $specialistIds, typeCode: $typeCode) {
      data {
        impressions
        clicks
        enquiries
      }
    }
  }
`;

export const REDEEM_UPGRADE_LISTING = gql`
  mutation redeemUpgradeListing(
    $input: RedeemUpgradeListingInput!
    $shouldExtendsFields: Boolean = false
  ) {
    redeemUpgradeListing(input: $input) {
      listing {
        ...ListingQueryFragment
      }
    }
  }
  ${LISTING_QUERY_FRAGMENT}
`;

export const LEAD_LIST_V2_QUERY = gql`
  query leadListV2(
    $listingId: String
    $consumerEmail: String
    $consumerMobileHash: String
    $whatsappUsername: String
    $leadQuality: String
    $leadType: String
    $role: String
    $source: String
    $startDate: String
    $endDate: String
    $unread: String
    $page: String
    $limit: Int
  ) {
    leadListV2(
      listingId: $listingId
      consumerEmail: $consumerEmail
      consumerMobileHash: $consumerMobileHash
      whatsappUsername: $whatsappUsername
      leadQuality: $leadQuality
      leadType: $leadType
      role: $role
      source: $source
      startDate: $startDate
      endDate: $endDate
      unread: $unread
      page: $page
      limit: $limit
    ) {
      data {
        id
        sources
        enquirer {
          email
          mobile
          whatsappUsername
          name
          consumerMobileHash
        }
        type
        isRead
        createdDate
        updatedDate
        isCrossListingActive
        targetUserLegacyIdIpp
        insights {
          qualityType
          leadRole
        }
        listingDetail {
          id
          propertyType {
            text
          }
          status {
            code
            text
          }
          channel {
            text
          }
          formattedTitle
        }
      }
      pagination {
        total
        nextPageToken
        limit
        page
      }
    }
  }
`;

export const LEAD_SEARCH_V2 = gql`
  query leadSearchV2($freeText: String, $match: String) {
    leadSearchV2(freeText: $freeText, match: $match) {
      enquirers {
        email
        mobile
        name
        mobileHash
        whatsappUsername
      }
      listingDetails {
        id
        propertyType {
          text
        }
        status {
          code
          text
        }
        channel {
          code
          text
        }
        formattedTitle
        salePrice {
          currencyCode
          fixed
        }
        rentPrice {
          currencyCode
          fixed
        }
        bedroom {
          text
        }
        bathroom {
          text
        }
        builtUp {
          measurementUnit {
            code
          }
          value
          text
        }
        location {
          level5 {
            text
          }
          formattedAddress
        }
      }
    }
  }
`;

export const LEAD_VIEW_V2 = gql`
  query leadV2($id: ID!) {
    leadV2(id: $id) {
      id
      enquirer {
        email
        mobile
        whatsappUsername
        name
      }
      sources
      isVerified
      type
      isRead
      createdDate
      updatedDate
      isCrossListingActive
      marketplace
      targetUserLegacyIdIpp
      enquiryVersion
      insights {
        qualityType
        histories {
          source
          timestamp
          enquiryVersion
        }
        preferences {
          listing {
            configurationDesc
            locationDesc
            priceDesc
            propertyTypeDesc
          }
        }
        recentAgentContacts
        searchingSince
        leadRole
      }
      listingDetail {
        id
        propertyType {
          text
        }
        status {
          text
        }
        channel {
          code
          text
        }
        formattedTitle
        description
        salePrice {
          currencyCode
          fixed
        }
        rentPrice {
          currencyCode
          fixed
        }
        bedroom {
          text
        }
        bathroom {
          text
        }
        builtUp {
          measurementUnit {
            code
          }
          value
          text
        }
        images {
          url
        }
        location {
          formattedAddress
          level1 {
            text
          }
          level2 {
            text
          }
          level3 {
            text
          }
          level5 {
            text
          }
        }
      }
      listingPreviewLink
    }
  }
`;

export const READ_LEAD_V2 = gql`
  mutation readLeadV2($input: ReadLeadV2Input!) {
    readLeadV2(input: $input) {
      isLeadListMarked
    }
  }
`;

export const LOCATION_ISSUE_REFERENCES_QUERY = gql`
  query locationIssueReferences {
    references {
      locationIssueTypes {
        id
        code
        description
      }
    }
  }
`;

export const MARKETWATCH_NOTIFICATION_UNREAD_COUNT_QUERY = gql`
  query mwNotificationUnreadCount($getCount: Boolean) {
    mwNotificationUnreadCount(getCount: $getCount) {
      count
      hasUnreadNotifications
    }
  }
`;

export const TOGGLE_LCV3_MUTATION = gql`
  mutation updateUser($input: UpdateUserInput!) {
    response: updateUser(input: $input) {
      id
      isToggledLCv3
      featureToggle {
        isListingV3FlowOpted
      }
    }
  }
`;
